// Melbourne, "Drizzle under the clocks". A cool, drizzly October night at the busiest tram crossing in the world,
// Flinders and Swanston Streets.
//
// Where we stand: on the forecourt steps of St Paul's Cathedral, on the north-east corner of the crossing, about 3 m
// above the road, looking south-west (233 degrees) across the crossing to Flinders Street Station's corner entrance,
// which faces the middle of the crossing at 45 degrees to the streets. (The brief asked for Federation Square, but
// Fed Square stands on the same side of Flinders Street as the station: from there the long façade is seen edge-on and
// the clock tower hides behind the dome. This corner is where the station's façade, its dome and its clock tower are
// all seen, and Fed Square is still in the frame.)
// Left to right, as seen from here: Federation Square's corner across Flinders Street (its triangle-tiled walls of
// sandstone, zinc and glass), Swanston Street running south to Princes Bridge (the Yarra is hidden below the bank),
// the towers of Southbank across the river with Eureka Tower's gold crown and red stripe, Australia 108 further off;
// then the station: its short Swanston Street front, the chamfered corner with the great arched entrance, the row of
// clocks over the steps ("meet under the clocks"), the drum and the green copper dome; the long banded façade of red
// brick and cream render running away west along Flinders Street, outlined in lights, to the banded clock tower at
// the Elizabeth Street end (drawn nearer than it is: the façade is compressed to about half its length); and on the
// right Young & Jackson's hotel on the north-west corner, a rooftop sign on its roof (the city's billboard).
// Overhead, the trams' wires. On the wet road: the trams' rails, the crossings' white lines, the bluestone kerbs.
// St Paul's itself is behind us: its forecourt and the corner's bluestone footpath are the foreground, with a lamp,
// a bin and a coffee cart; its bells ring the hour.
// Not seen from here: the Yarra (below the bank behind the station), the laneways (behind us), Crown's fire towers.
// render(t, { crowd }) thins the people (CROWD); crowd 0 draws nobody (see "People").
//
// The loop's timeline (seconds; every period divides 240):
//   traffic and walk signals          a 60 s cycle: Flinders green 0-26, Swanston green 30-56; walk on the
//                                     crossings parallel to the green, 10 s, then 10 s of flashing red
//   people crossing                   a batch on a near crossing every 30 s (Swanston 0-18, Flinders 30-48), far
//                                     batches on the other two
//   trams on Swanston Street          northbound 30-58, 150-178 (stops at the platform by Young & Jackson),
//                                     southbound 90-114, 210-234; plus a far one passing at 34-52 and 154-172
//   trams on Flinders Street          westbound 2-24, eastbound 184-206
//   the City Circle tram (big)        the maroon heritage tram appears far down Flinders Street at 86, stops at
//                                     the platform in front of the station 96-116 (bell at 113), crosses at 120-
//                                     126 and rolls out past Federation Square by 134
//   a heavier shower (big)            builds 140-150, pours 150-170 (every umbrella up, the road shines), eases by 182
//   the hour (big)                    St Paul's bells at 200, 203, 206, 209; the clock tower's face and the dome's
//                                     lantern brighten; the gulls on the station's parapet lift off (200) and are
//                                     back by 232
//   cars along Flinders Street        a taxi 4-20, a car 64-82 (hook turn into Swanston 66-78), a ute 124-140
//   a cyclist                         down Swanston 70-90, back up 190-206
//   the busker                        plays all loop; coins in his case at 38, 112, 222
//   the coffee cart                   a customer every 40 s: arrives, waits, takes the cup, walks off
//   a gull                            pecks at a chip by the bin 24-60 and 162-196, flies off to the dome
//   all loop                          drizzle at three depths, rings on the road, the wet road mirrors the
//                                     station, the façade's lights shimmer, clouds drift low, windows switch on
//                                     and off, Eureka's crown glows, people wait under the clocks
// The longest gap between two timed things is about 10 s.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, streak, reflect, FONT3, hands, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= The camera =================
   Everything on the ground and the station is placed in metres and projected, so the perspective holds together:
   x east and y north from the middle of the crossing, z up from the road. */
const VX = 27, VY = 21, EYE = 3.2;                    // St Paul's forecourt steps, eye 3.2 m above the road
const BRG = 233 * Math.PI / 180;                      // looking south-west
const FL = 210, CXP = 226, HZ = 166;                  // focal length (px), the lens axis' column, the horizon's row
const FX = Math.sin(BRG), FY = Math.cos(BRG), RX = Math.sin(BRG + Math.PI / 2), RY = Math.cos(BRG + Math.PI / 2);
/** screen [x, y, depth] of a world point */
function proj(x, y, z) {
  const dx = x - VX, dy = y - VY, u = dx * FX + dy * FY;
  return [CXP + FL * (dx * RX + dy * RY) / u, HZ - FL * (z - EYE) / u, u];
}
/** the world point [x, y, depth] on the road (z 0) that screen pixel (sx, sy) shows; sy below the horizon */
function groundAt(sx, sy) {
  const u = FL * EYE / (sy - HZ), v = (sx - CXP) * u / FL;
  return [VX + u * FX + v * RX, VY + u * FY + v * RY, u];
}
/** how far along a vertical wall (from P0, unit direction D) screen column sx looks */
function onWall(sx, P0x, P0y, Dx, Dy) {
  const q = (sx - CXP) / FL, A = RX - q * FX, B = q * FY - RY, ax = P0x - VX, ay = P0y - VY;
  return (ay * B - ax * A) / (Dx * A - Dy * B);
}
const depthOf = (x, y) => (x - VX) * FX + (y - VY) * FY;
const rowOf = (z, u) => HZ - FL * (z - EYE) / u;      // the screen row of height z at depth u

/* ================= Layout (world, metres) ================= */
const KERB = 10, LOT = 15;                            // half-widths of both streets' carriageways, and the building lines
const TRK = 1.75, GAUGE = 0.72;                       // tram track centres (either side of the middle) and half the gauge
// Flinders Street Station: the corner chamfer facing the crossing (from its Swanston end A to its Flinders end B),
// the drum and the copper dome behind it, the Swanston Street front, the Flinders Street façade (drawn about half its
// real length, so the clock tower at the far end reads), and the clock tower
const ST = { ax: -15, ay: -27, bx: -27, by: -15, len: 12 * Math.SQRT2, top: 20.6 };
const DOME = { x: -30, y: -30, drum0: 20.6, drum1: 28.2, rDrum: 7.6, z0: 28.2, z1: 36, r: 7.3, lan1: 38.5, tip: 40.3 };
const FAC = { y: -15, x0: -27, x1: -122, h: 19.5, bay: 5.5 };
const RET = { x: -15, y0: -27, y1: -55, h: 18 };
const TOWER = { x0: -131, x1: -122, y0: -24, y1: -15, shaft: 40, top: 47.5 };
const FED = { y: -15, x0: 15, x1: 60 };               // Federation Square's front on Flinders Street
const YJ = { x: -15, y0: 15, y1: 52, h: 17 };         // Young & Jackson's front on Swanston Street
const PLAT = { x0: -42, x1: -17 };                    // the tram platform in the middle of Flinders Street, by the station
const SPLAT = { y0: 19, y1: 42 };                     // the platform on Swanston Street, by Young & Jackson
const ACCENT = '#00a651';                             // the trams' green

/* ================= Layout (screen) ================= */
const BB = { x: 392, y: 40, w: 54, h: 22 };           // the rooftop sign on Young & Jackson's (the city's billboard)
const BB_ROOF = 70;                                   // where its legs stand on the roof

/* ================= The loop's timeline (seconds) ================= */
const SIG_T = 60;                                     // the signals' cycle
const CC = { far: 86, stop0: 96, stop1: 116, bell: 113, out: 134 };   // the City Circle tram
const SHOWER = { a: 140, b: 150, c: 170, d: 182 };    // the heavier shower: builds, pours, eases
const BELLS = [200, 203, 206, 209];                   // the hour, from St Paul's
const GULLS_UP = { t0: 200, t1: 232 };                // the gulls on the parapet lift off and come back
const COINS = [38, 112, 222];
const CHIP = [[24, 60], [162, 196]];                  // the gull at the chip by the bin
const COFFEE_T = 40;
// The share of the people the scene shows by itself: 1 = everyone (about 18 near and middle, more far off), half by
// default, 0 = nobody. Ranks are fixed at build time; rank 0 (the busker, the barista, the City Circle's driver, the
// people of the big moments) stays at any crowd above 0.
const CROWD = 0.5;
const rankOf = (i, off = 0) => frac((i + 0.5) * 0.6180339887 + off);

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (clamp(Math.round(r), 0, 255) << 16) | (clamp(Math.round(g), 0, 255) << 8) | clamp(Math.round(b), 0, 255)).toString(16).slice(1);
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (MIXED[k]) return MIXED[k];
  const A = C(a), B = C(b), u = clamp(t, 0, 1);
  return (MIXED[k] = toHex(lerp(A[0], B[0], u), lerp(A[1], B[1], u), lerp(A[2], B[2], u)));
}
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    get(i, j) { const o = (j * w + i) * 4; return [d[o], d[o + 1], d[o + 2], d[o + 3]]; },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(Math.round(x1) - Math.round(x0)), Math.abs(Math.round(y1) - Math.round(y0)), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    /** a straight line in the world, projected (sag: how far its middle hangs down, in metres) */
    line3(A, B, col, sag = 0) {
      const p = proj(...A), q = proj(...B);
      const n = Math.max(1, Math.ceil(Math.max(Math.abs(q[0] - p[0]), Math.abs(q[1] - p[1])) * 1.4));
      for (let k = 0; k <= n; k++) {
        const u = k / n, s = proj(lerp(A[0], B[0], u), lerp(A[1], B[1], u), lerp(A[2], B[2], u) - sag * 4 * u * (1 - u));
        if (s[2] > 0.5) this.set(s[0], s[1], col);
      }
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered seam) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); optional period px in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** crop a painter to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
/** piecewise script: keys [[t, v], ...] sorted by t; the value at t (eased between keys) and whether it changes */
function along(keys, t, ease = true) {
  if (t <= keys[0][0]) return { v: keys[0][1], moving: false };
  for (let i = 1; i < keys.length; i++) if (t < keys[i][0]) {
    const a = keys[i - 1], b = keys[i], f = (t - a[0]) / (b[0] - a[0]);
    return { v: lerp(a[1], b[1], ease ? f * f * (3 - 2 * f) * 0.5 + f * 0.5 : f), moving: a[1] !== b[1] };
  }
  return { v: keys[keys.length - 1][1], moving: false };
}
/** the depth of the nearest building in every pixel, written while the walls are painted (build time): marks on the
 *  road and the things that pass behind a building look it up */
const ZBUF = new Float32Array(W * H).fill(Infinity);
const zset = (sx, sy, u) => { const i = sy * W + sx; if (sx >= 0 && sx < W && sy >= 0 && sy < H && u < ZBUF[i]) ZBUF[i] = u; };
const zat = (sx, sy) => (sx >= 0 && sx < W && sy >= 0 && sy < H ? ZBUF[(sy | 0) * W + (sx | 0)] : Infinity);
/**
 * Paint a vertical wall into painter p: from world point P0 along unit direction D for len metres, heights zLo..zHi.
 * mat(s, z, sx, sy, u) gives the colour at s metres along and z up (null leaves it clear). Returns the depth of the
 * wall in every column it covers ({ [sx]: u }), for the things that pass behind it.
 */
function paintWall(p, P0x, P0y, Dx, Dy, len, zLo, zHi, mat) {
  const a = proj(P0x, P0y, 0), b = proj(P0x + Dx * len, P0y + Dy * len, 0), depth = {};
  const behind = a[2] <= 0.5 || b[2] <= 0.5;                                          // one end behind us: scan every column
  const x0 = behind ? 0 : Math.max(0, Math.floor(Math.min(a[0], b[0]))), x1 = behind ? W - 1 : Math.min(W - 1, Math.ceil(Math.max(a[0], b[0])));
  for (let sx = x0; sx <= x1; sx++) {
    const s = onWall(sx + 0.5, P0x, P0y, Dx, Dy);
    if (s < 0 || s > len) continue;
    const u = depthOf(P0x + Dx * s, P0y + Dy * s);
    if (u <= 0.5) continue;
    depth[sx] = u;
    const yT = Math.max(0, Math.floor(rowOf(zHi, u))), yB = Math.min(H - 1, Math.ceil(rowOf(zLo, u)));
    for (let sy = yT; sy <= yB; sy++) {
      const z = EYE - (sy + 0.5 - HZ) * u / FL;
      if (z < zLo || z > zHi) continue;
      const col = mat(s, z, sx, sy, u);
      if (col) { p.set(sx, sy, col); zset(sx, sy, u); }
    }
  }
  return depth;
}

/* ================= The sky: a low drizzle sky, the city's sodium glow under it ================= */
const SKY = ['#080a1a', '#0b0d1f', '#0f1124', '#13152a', '#181a30', '#1e1f36', '#25243c', '#2d2a42', '#363148', '#40374c',
  '#4b3d4f', '#574451', '#634a52'];
function renderSky() {
  const p = pix(W, HZ + 4);
  for (let y = 0; y < HZ + 4; y++) for (let x = 0; x < W; x++) {
    let v = Math.pow(y / HZ, 1.35) * 0.72;
    v += 0.2 * Math.exp(-(((x - 230) / 190) ** 2)) * smooth(60, 160, y);                // the city's glow on the cloud base
    v += 0.1 * Math.exp(-(((x - 120) / 70) ** 2)) * smooth(80, 150, y);                 // Southbank's
    v += (vnoise(x / 60, y / 9, 3) - 0.5) * 0.06;
    p.set(x, y, pick(SKY, v, x, y));
  }
  return p.done();
}
/* low, broken drizzle cloud, tileable (period W): soft banks, lit warm from below by the city */
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 60 + r() * 100, base = h * (0.45 + r() * 0.35);
    const np = 6 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = 12 + r() * 24, ry = 3 + r() * 5;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.2, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * 0.3;
      if (v > best) best = v;
    }
    best += 0.15 * Math.sin(TAU * 11 * x / W + y * 0.41 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.23);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.34, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.set(x, y, col, def.a);
  }
  return { c: p.done(), y: def.y, k: def.k };
}
const CLOUD_DEFS = [
  { seed: 13, y: -10, h: 56, banks: 7, k: 1, a: 255, pal: { top: '#1c1d31', body: '#16172a', core: '#131425', bot: '#262438', bot2: '#1f1e32' } },
  { seed: 31, y: 20, h: 56, banks: 6, k: 2, a: 235, pal: { top: '#2a283e', body: '#211f34', core: '#1c1b2e', bot: '#43384c', bot2: '#332c42' } },
  { seed: 47, y: 52, h: 50, banks: 6, k: 3, a: 215, pal: { top: '#3a3248', body: '#2c273c', core: '#272236', bot: '#5e4852', bot2: '#463a4a' } },
];

/* ================= Windows that switch on and off ================= */
const WINS = [];
const LIT = [['#ffe2a0', '#f2b866'], ['#ffd690', '#e8a050'], ['#fff0c8', '#f0cc88'], ['#f6c07a', '#d08440'], ['#dfe8f6', '#a8b8d4']];
function addWin(r, x, y, w, h, opt = {}) {
  const v = r(), on = opt.pOn ?? 0.4, mode = opt.mode || (v < on ? 'on' : v < on + 0.3 ? 'tog' : 'off');
  const lp = opt.cool ? LIT[4] : LIT[Math.floor(r() * r() * 4)];
  WINS.push({ x, y, w, h, mode, k: 2 + Math.floor(r() * 3), ph: r(), duty: 0.35 + r() * 0.35, c1: lp[0], c2: lp[1], id: WINS.length });
}
const winLit = (w, t) => w.mode === 'on' || (w.mode === 'tog' && frac(w.k * t / LOOP + w.ph) < w.duty);
function drawWins(a, b, t, dim = 1) {
  if (dim < 1) ctx.globalAlpha = dim;
  for (let i = a; i < b; i++) {
    const w = WINS[i];
    if (!winLit(w, t)) continue;
    if (w.h > 1) { const half = w.h >> 1; R(w.x, w.y, w.w, half, w.c1); R(w.x, w.y + half, w.w, w.h - half, w.c2); }
    else R(w.x, w.y, w.w, 1, w.c1);
  }
  ctx.globalAlpha = 1;
}

/* ================= Southbank across the river: towers in the haze, Eureka Tower, Australia 108 ================= */
// Eureka Tower: 297 m, about 40 m across, 490 m off. Its blue glass and white lines are the Eureka flag, the red stripe
// up its face the blood of the stockade, the gold-plated top ten floors the gold rush. Australia 108, taller but
// further, stands off to its left. Columns and rows read off the camera (see the header).
const EUR = { x0: 125, x1: 141, top: 42, crown: 54 };
const A108 = { x0: 90, x1: 101, top: 63 };
const SB_LIGHTS = [];                                 // aviation lights on the towers: [x, y, phase]
function paintSouthbank(p, r) {
  const HAZE = ['#13152a', '#181a31', '#1e2038', '#25263f'];
  // the far towers, lowest and haziest first, from the Princes Bridge gap to behind the station's façade
  const towers = [[82, 92, 118], [100, 112, 104], [111, 121, 96], [143, 154, 88], [154, 163, 110], [163, 176, 100], [176, 186, 92],
    [232, 244, 110], [244, 256, 124], [256, 266, 104], [266, 280, 118], [280, 292, 128], [292, 306, 120], [306, 318, 134], [318, 330, 126]];
  for (const [x0, x1, top] of towers) {
    const v0 = 0.25 + r() * 0.35;
    for (let y = top; y < HZ + 2; y++) for (let x = x0; x < x1; x++)
      p.set(x, y, x === x0 ? '#101224' : y === top ? '#2c2c44' : pick(HAZE, v0 + 0.25 * smooth(top, HZ, y), x, y));
    for (let y = top + 3; y < HZ - 2; y += 3) for (let x = x0 + 1; x < x1 - 1; x += 2) if (r() < 0.3) addWin(r, x, y, 1, 1, { pOn: 0.45, cool: r() < 0.3 });
    if (top < 110 && r() < 0.7) SB_LIGHTS.push([x0 + ((x1 - x0) >> 1), top - 1, r()]);
  }
  // Australia 108: a slim dark tower, its corners rounded, the gold starburst of its sky deck two thirds up
  for (let y = A108.top; y < HZ + 2; y++) for (let x = A108.x0; x <= A108.x1; x++) {
    if (y < A108.top + 3 && (x === A108.x0 || x === A108.x1) && y < A108.top + 2) continue;
    let col = pick(['#141830', '#1a1f3a', '#212744'], 0.35 + (x - A108.x0) * 0.05 + 0.2 * smooth(A108.top, HZ, y), x, y);
    if (x === A108.x0) col = '#10132a';
    if (y === 84 || y === 85) col = x % 2 ? '#8a6a32' : '#6a5028';
    p.set(x, y, col);
  }
  for (let y = A108.top + 4; y < HZ - 4; y += 2) for (let x = A108.x0 + 2; x < A108.x1 - 1; x += 2) if (r() < 0.22) addWin(r, x, y, 1, 1, { pOn: 0.4 });
  SB_LIGHTS.push([95, A108.top - 1, 0.3]);
  // Eureka Tower
  for (let y = EUR.top; y < HZ + 2; y++) for (let x = EUR.x0; x <= EUR.x1; x++) {
    const top = EUR.top + (x > EUR.x1 - 4 ? x - (EUR.x1 - 4) : 0);           // the top steps down on the east side
    if (y < top) continue;
    let col;
    if (y <= EUR.crown) {                                                   // the gold crown, its window rows
      const v = 0.5 + 0.4 * (1 - (y - EUR.top) / (EUR.crown - EUR.top)) - (x === EUR.x0 ? 0.25 : 0) + (x > 134 ? 0.08 : 0);
      col = (y - EUR.top) % 2 === 1 ? pick(['#6a4a1c', '#8a6224', '#a87a30'], v, x, y) : pick(['#a87a30', '#d8a440', '#f2c866', '#ffe49a'], v, x, y);
      if (y === top) col = '#fff0b8';
    } else {
      col = pick(['#121730', '#182040', '#1f294c', '#283459'], 0.3 + (x - EUR.x0) * 0.025 + 0.15 * smooth(EUR.crown, HZ, y), x, y);
      if (x === EUR.x0) col = '#0e1228';
      if (x === 129 || x === 133) col = '#3a4670';                         // the white lines, lit faintly
      if (x === 138 || x === 139) col = y % 3 ? '#8a2228' : '#a83034';     // the red stripe
    }
    p.set(x, y, col);
  }
  for (let y = EUR.crown + 3; y < HZ - 3; y += 2) for (let x = EUR.x0 + 1; x < 137; x += 2) if (x !== 129 && x !== 133 && r() < 0.24) addWin(r, x, y, 1, 1, { pOn: 0.5 });
  SB_LIGHTS.push([132, EUR.top - 1, 0.7]);
}

/* ================= Flinders Street Station: banded red brick and cream render, floodlit, outlined in lights ================= */
// The ratios it is drawn to (from photos; the station's drawings give no heights): the copper dome is about 0.75 of
// the façade's height across (14.6 m to 19.5 m), the entrance arch about 0.62 of the dome's width (9 m), and the clock
// tower about 2.4 times the façade's height (47.5 m to 19.5 m). The façade runs about half its real length here.
const BRICK = ['#2e100c', '#461810', '#622214', '#7e2e18', '#9a3c1e', '#b44c24', '#cc602c', '#e07836', '#f09444'];
const CREAM = ['#5a4232', '#7e6044', '#a2805a', '#c4a070', '#dcbc86', '#efd49e', '#fbe6b8', '#fff4d6'];
const GRANITE = ['#24222c', '#34303a', '#47414a', '#5e5660', '#7a7078'];
const COPPER = ['#0c2220', '#123029', '#1a4034', '#245242', '#2f6650', '#3e7a60', '#559276', '#74ac92', '#9ccab2'];
const HAZE_C = '#3a2e42';
const fade = (col, u) => (u > 80 ? mixHex(col, HAZE_C, 0.42 * smooth(80, 210, u)) : col);
/** how much of the floodlights a wall gets at height z: pools from the footpath, a lift where the cornice catches */
const lit = z => 0.36 + 0.32 * Math.exp(-z / 4.5) + 0.08 * Math.exp(-(((z - 15.4) / 0.8) ** 2));
const brick = (lt, sx, sy) => pick(BRICK, lt, sx, sy, 0.7);
const cream = (lt, sx, sy) => pick(CREAM, lt + 0.12, sx, sy, 0.6);
/** plain banded wall: courses of cream render in the red brick */
const banded = (z, lt, sx, sy, period = 1.3, share = 0.3) => (frac(z / period) < share ? cream(lt, sx, sy) : brick(lt, sx, sy));

/* the windows in the station's walls: painted dark into the wall, lit from a second layer when they're on */
const SWIN = [];
let LITP = null;
const winIds = new Map();
function winPix(key, sx, sy, col, r) {
  let id = winIds.get(key);
  if (id === undefined) {
    const v = r(key), mode = v < 0.42 ? 'on' : v < 0.72 ? 'tog' : 'off';
    id = SWIN.length; winIds.set(key, id);
    SWIN.push({ x0: sx, y0: sy, x1: sx, y1: sy, mode, k: 2 + Math.floor(r(key + 7) * 3), ph: r(key + 9), duty: 0.4 + r(key + 11) * 0.3 });
  }
  const b = SWIN[id];
  if (sx < b.x0) b.x0 = sx; if (sx > b.x1) b.x1 = sx; if (sy < b.y0) b.y0 = sy; if (sy > b.y1) b.y1 = sy;
  LITP.set(sx, sy, col);
}
const keyHash = k => hash(k * 7919, 3, 77);
/** an arched window or opening: inside the rectangle w x (top - bot) with a round head, centred at sc */
const inArch = (s, z, sc, w, bot, top) => Math.abs(s - sc) < w / 2 && z > bot && (z < top - w / 2 || Math.hypot(s - sc, z - (top - w / 2)) < w / 2);

/** the long front on Flinders Street and the short one on Swanston Street: s metres from the corner, z up */
function frontMat(which, len, height) {
  return (s, z, sx, sy, u) => {
    const bay = Math.floor(s / FAC.bay), bs = s - bay * FAC.bay, lt = lit(z);
    const pav = which === 'F' && bay >= 8 && bay <= 9;                               // the middle pavilion, a storey taller
    let roof = height + (bay % 2 === 1 ? Math.max(0, 1.5 - Math.abs(bs - 2.75) * 0.7) : 0);
    if (pav) roof = height + 2.8 + Math.max(0, 2 - Math.abs(s - 9 * FAC.bay) * 0.5);
    if (z > roof) return null;
    let col;
    if (z < 0.5) col = pick(GRANITE, 0.5, sx, sy);
    else if (z < 4.2) {                                                              // the ground floor: shop fronts and ways in
      if (bs > 0.7 && inArch(s, z, bay * FAC.bay + 3.1, 3.4, 0.5, 4.0)) {
        const shop = hash(bay, which === 'F' ? 1 : 2, 31) < 0.7;
        col = shop ? pick(['#a8703c', '#d89a52', '#f6c47a', '#ffe0a4'], 0.35 + 0.5 * (1 - z / 4), sx, sy) : pick(['#1c1416', '#2e2020', '#4a3428'], 0.4 + 0.3 * (1 - z / 4), sx, sy);
        if (shop && frac(s / 1.1) < 0.1) col = '#3a2822';
      } else col = bs < 0.7 ? cream(lt, sx, sy) : banded(z, lt, sx, sy);
    } else if (z < 4.9) col = z < 4.35 ? '#f6d494' : pick(['#1a1218', '#2a2026'], 0.5, sx, sy);   // the verandah: its lit soffit and fascia
    else if (z > roof - 0.2) col = cream(lt + 0.15, sx, sy);
    else if (z > 15 && z < 16.1) col = z < 15.25 ? '#3a2018' : cream(lt + 0.1, sx, sy); // the cornice and its shadow
    else if (z >= 16.1) col = frac(s / 1.1) < 0.18 && z < roof - 0.7 && !pav ? '#5a3424' : cream(lt - 0.06, sx, sy);   // the parapet's panels
    else if (bs < 0.75) col = cream(lt, sx, sy);                                     // pilasters
    else {
      let win = -1;
      for (const [k, wc] of [[0, 2.15], [1, 4.0]]) for (const [f, b0, t0] of [[0, 6.2, 9.6], [1, 10.6, 13.9]])
        if (inArch(s, z, bay * FAC.bay + wc, pav ? 1.25 : 0.95, b0, t0)) win = k * 2 + f;
      if (win >= 0) {
        const key = (which === 'F' ? 1 : 2) * 10000 + bay * 10 + win, glass = pick(['#1a1420', '#24202c'], 0.5, sx, sy);
        winPix(key, sx, sy, z > 9.6 && z < 10.6 ? glass : pick(['#e8a858', '#ffd28a', '#fff0c4'], 0.6 - (z % 4.4) * 0.08, sx, sy), keyHash);
        col = glass;
      } else {
        const near = Math.min(...[2.15, 4.0].map(wc => Math.abs(bs - wc)));
        col = near < 0.75 && ((z > 9.0 && z < 9.9) || (z > 13.3 && z < 14.2)) ? cream(lt + 0.05, sx, sy) : banded(z, lt, sx, sy);
      }
    }
    return fade(col, u);
  };
}
/** the corner facing the crossing: the great arch (the fanlight, the row of clocks, the doors), its piers, the attic */
const ARCH = { sc: ST.len / 2, half: 4.5, spring: 9.6, ring: 0.95, clock0: 7.5, clock1: 9.5 };
const CLOCKS = [];                                    // the row of clocks over the doors: [sx, sy] of each face
function cornerMat(s, z, sx, sy, u) {
  const { sc, half, spring, ring } = ARCH, ds = s - sc, lt = lit(z);
  const roof = ST.top + Math.max(0, 1.6 - Math.abs(ds) * 0.5);                        // a low pediment over the arch
  if (z > roof) return null;
  const rr = Math.hypot(ds, Math.max(0, z - spring));
  if (z < 1.5 && Math.abs(ds) < 6.2) return pick(GRANITE, 0.35 + frac(z / 0.3) * 0.5, sx, sy);   // the steps up to the doors
  if (z >= 1.5 && Math.abs(ds) < half && (z < spring || rr < half)) {                // inside the arch
    if (z >= spring) {                                                               // the fanlight, lit from the hall
      const ang = Math.atan2(z - spring, ds);
      if (frac(ang / (Math.PI / 10)) < 0.14 || Math.abs(rr - 2.3) < 0.18) return '#4a3020';
      return pick(['#b87a40', '#dea25a', '#f8cc80', '#fff0c0'], 0.4 + 0.45 * (1 - rr / half), sx, sy);
    }
    if (z >= ARCH.clock0) return z > ARCH.clock1 - 0.25 ? '#4a3020' : pick(['#1a1214', '#261a1a'], 0.5, sx, sy);   // the clocks' board
    const pane = frac((ds + half) / 1.12) > 0.16 && frac((ds + half) / 1.12) < 0.86 && frac(z / 1.55) > 0.14;
    return pane ? pick(['#b07a44', '#d89a58', '#f4c47c'], 0.3 + 0.12 * Math.sin(ds * 2), sx, sy) : '#2a1a16';   // the doors
  }
  if (z >= 1.5 && Math.abs(ds) < half + ring && (z < spring || rr < half + ring)) {   // the archivolt, a keystone at its crown
    const ang = z > spring ? Math.atan2(z - spring, ds) : 0;
    if (z > spring && frac(ang / (Math.PI / 9)) < 0.1) return cream(lt - 0.12, sx, sy);
    return cream(lt + (Math.abs(ds) < 0.5 && z > spring + half ? 0.2 : 0.06), sx, sy);
  }
  if (z > 15.4 && z < 16.6) return z < 15.65 ? '#3a2018' : cream(lt + 0.12, sx, sy);  // the cornice
  if (z >= 16.6) {                                                                   // the attic: a balustrade, a round window over the arch
    if (z > roof - 0.35) return cream(lt + 0.2, sx, sy);
    if (Math.hypot(ds, z - 18.4) < 0.9) return pick(['#e8a858', '#ffd28a'], 0.6, sx, sy);
    if (Math.hypot(ds, z - 18.4) < 1.25) return cream(lt + 0.1, sx, sy);
    if (z > 19.2 && z < 20.1 && frac(s / 0.7) < 0.4 && Math.abs(ds) > 1.6) return '#4a2a1e';
    return frac(z / 0.9) < 0.3 ? cream(lt - 0.12, sx, sy) : cream(lt - 0.02, sx, sy);
  }
  if (s < 0.9 || s > ST.len - 0.9) return cream(lt + 0.05, sx, sy);                  // the corner pilasters
  for (const wc of [1.95, ST.len - 1.95]) for (const [b0, t0] of [[3.6, 7.2], [10.4, 14.2]])   // windows in the piers
    if (inArch(s, z, wc, 1.0, b0, t0)) {
      winPix(30000 + Math.round(wc) * 10 + b0, sx, sy, pick(['#e8a858', '#ffd28a'], 0.5, sx, sy), keyHash);
      return pick(['#1a1420', '#24202c'], 0.5, sx, sy);
    }
  return banded(z, lt, sx, sy, 1.15, 0.34);
}
/** a round body (the drum, the dome, the lantern) around the vertical axis at (wx, wy): mat(phi, z, sx, sy, u, r) */
function paintRound(p, wx, wy, z0, z1, rOf, mat) {
  const [ax, , u] = proj(wx, wy, 0), k = FL / u;
  for (let sy = Math.floor(rowOf(z1, u)); sy <= Math.ceil(rowOf(z0, u)); sy++) {
    const z = EYE - (sy + 0.5 - HZ) * u / FL;
    if (z < z0 || z > z1) continue;
    const r = rOf(z);
    if (!(r > 0)) continue;
    for (let sx = Math.floor(ax - r * k) - 1; sx <= Math.ceil(ax + r * k) + 1; sx++) {
      const xo = (sx + 0.5 - ax) / k;
      if (Math.abs(xo) > r) continue;
      const col = mat(Math.asin(clamp(xo / r, -1, 1)), z, sx, sy, u, r);
      if (col) { p.set(sx, sy, col); zset(sx, sy, u); }
    }
  }
}
const domeR = z => DOME.r * Math.sqrt(Math.max(0, 1 - ((z - DOME.z0) / (DOME.z1 - DOME.z0)) ** 2));
function paintDome(p) {
  const shade = phi => 0.5 + 0.3 * Math.cos(phi + 0.35);
  paintRound(p, DOME.x, DOME.y, DOME.drum0, DOME.drum1, () => DOME.rDrum, (phi, z, sx, sy) => {   // the drum: arched windows all round
    const lt = shade(phi) - 0.12 + (z > DOME.drum1 - 0.5 ? 0.15 : 0);
    if (z > DOME.drum1 - 0.6) return cream(lt + 0.1, sx, sy);
    const w = frac((phi + 0.12) / (Math.PI / 6));
    if (Math.abs(w - 0.5) < 0.19 && z > 22.2 && (z < 26.0 || Math.abs(w - 0.5) < 0.19 * Math.sqrt(Math.max(0, 1 - (z - 26.0) / 1.3)))) return pick(['#c0844a', '#ecb466', '#ffdc96'], 0.5 + 0.35 * Math.cos(phi), sx, sy);
    return Math.abs(w - 0.5) < 0.27 ? cream(lt, sx, sy) : banded(z, lt, sx, sy, 1.1, 0.35);
  });
  paintRound(p, DOME.x, DOME.y, DOME.z0, DOME.z1, domeR, (phi, z, sx, sy, u, r) => {   // the copper dome and its ribs
    const f = (z - DOME.z0) / (DOME.z1 - DOME.z0);
    let v = 0.3 + 0.32 * Math.cos(phi + 0.3) * Math.sqrt(1 - f * 0.6) + 0.28 * (1 - f) * (1 - f) + 0.12 * f;
    const rib = frac((phi + 0.12) / (Math.PI / 6));
    if (rib < 0.1 || rib > 0.96) v += 0.2;
    if (Math.abs(phi) > 1.3) v -= 0.15;
    return pick(COPPER, v, sx, sy, 0.7);
  });
  for (let z = DOME.z0; z < DOME.z0 + 0.5; z += 0.1) paintRound(p, DOME.x, DOME.y, z, z + 0.1, () => DOME.r + 0.2, (phi, zz, sx, sy) => cream(0.5 + 0.2 * Math.cos(phi), sx, sy));
  paintRound(p, DOME.x, DOME.y, DOME.z1 - 0.3, DOME.lan1, () => 1.35, (phi, z, sx, sy) =>   // the lantern
    Math.abs(phi) < 0.5 && z > DOME.z1 + 0.4 && z < DOME.lan1 - 0.5 ? '#ffe2a0' : pick(COPPER, 0.5 + 0.3 * Math.cos(phi), sx, sy));
  paintRound(p, DOME.x, DOME.y, DOME.lan1, DOME.lan1 + 0.9, z => 1.5 * Math.sqrt(Math.max(0, 1 - (z - DOME.lan1) / 0.9)), (phi, z, sx, sy) => pick(COPPER, 0.6 + 0.3 * Math.cos(phi), sx, sy));
  const [tx, ty0] = proj(DOME.x, DOME.y, DOME.lan1 + 0.9), [, ty1] = proj(DOME.x, DOME.y, DOME.tip);
  for (let y = Math.round(ty1); y <= Math.round(ty0); y++) p.set(Math.round(tx), y, y === Math.round(ty1) ? '#ffe9a8' : '#c8a050');
}
/** the clock tower at the Elizabeth Street end: its banded shaft, the clock, the open belfry, a small copper cupola */
const TCLOCK = { s: 4.5, z: 36.3, r: 1.85 };
function towerMat(face) {
  return (s, z, sx, sy, u) => {
    const lt = lit(z) + (face === 'E' ? 0.08 : -0.1) + 0.12 * Math.exp(-(((z - 36) / 4) ** 2));
    let col;
    if (s < 0.8 || s > 8.2) col = cream(lt + 0.05, sx, sy);                          // corner piers
    else if (z > 39.4 && z < 40.6) col = z < 39.7 ? '#3a2018' : cream(lt + 0.15, sx, sy);
    else if (z >= 33.6 && z <= 39.4) {                                               // the clock stage
      const d = Math.hypot(s - TCLOCK.s, z - TCLOCK.z);
      col = d < TCLOCK.r ? (face === 'E' ? '#f6eed6' : '#d8ccb0') : d < TCLOCK.r + 0.35 ? '#3a2a20' : cream(lt, sx, sy);
    } else if (inArch(s, z, 4.5, 1.3, 21, 28) || inArch(s, z, 4.5, 1.1, 9, 14.5)) {
      winPix(40000 + (face === 'E' ? 1 : 2) * 10 + (z > 18 ? 1 : 0), sx, sy, pick(['#e8a858', '#ffd28a'], 0.5, sx, sy), keyHash);
      col = '#1e1822';
    } else col = banded(z, lt, sx, sy, 2.0, 0.38);
    return fade(col, u);
  };
}
function paintTower(p) {
  const { x0, x1, y0, y1, shaft } = TOWER;
  paintWall(p, x1, y1 + 0.5, 0, -1, y1 + 0.5 - y0, 0, shaft + 0.6, towerMat('E'));
  paintWall(p, x1, y1 + 0.5, -1, 0, x1 - x0, 0, shaft + 0.6, towerMat('N'));
  // the belfry, set back on the shaft, its tall arches lit; then the cupola
  const bx = (x0 + x1) / 2, by = (y0 + y1) / 2;
  paintRound(p, bx, by, shaft + 0.6, shaft + 4.3, () => 3.4, (phi, z, sx, sy, u) => {
    const w = frac((phi + 0.25) / (Math.PI / 4));
    if (z > shaft + 3.6) return fade(cream(0.62 + 0.2 * Math.cos(phi), sx, sy), u);
    return fade(Math.abs(w - 0.5) < 0.22 && z > shaft + 1.1 ? pick(['#d89048', '#ffc874'], 0.5 + 0.3 * Math.cos(phi), sx, sy) : banded(z, 0.5 + 0.2 * Math.cos(phi), sx, sy, 1.2, 0.4), u);
  });
  paintRound(p, bx, by, shaft + 4.3, shaft + 6.6, z => 2.6 * Math.sqrt(Math.max(0, 1 - ((z - shaft - 4.3) / 2.3) ** 2)), (phi, z, sx, sy, u) => fade(pick(COPPER, 0.4 + 0.3 * Math.cos(phi + 0.3), sx, sy), u));
  const [fx, fy0] = proj(bx, by, shaft + 6.6), [, fy1] = proj(bx, by, TOWER.top);
  for (let y = Math.round(fy1); y <= Math.round(fy0); y++) p.set(Math.round(fx), y, '#a88a5a');
}
/** beyond the tower: the lower fronts on the south side of Flinders Street towards Queen Street, darker and hazier */
function paintBeyond(p, r) {
  const segs = [[131, 146, 13], [146, 170, 22], [170, 205, 15], [205, 250, 27], [250, 330, 18]];
  for (const [a, b, h] of segs) {
    const tone = ['#4a3a40', '#3a3646', '#52423e', '#3c3a4a', '#463a44'][Math.floor(r() * 5)];
    paintWall(p, -a, -15, -1, 0, b - a, 0, h, (s, z, sx, sy, u) => {
      if (frac(s / 3.2) > 0.35 && frac(s / 3.2) < 0.7 && frac(z / 3.4) > 0.4 && frac(z / 3.4) < 0.85 && z > 3)
        return hash(Math.floor(s / 3.2) + a, Math.floor(z / 3.4), 91) < 0.45 ? '#e8b878' : '#1e1c28';
      return mixHex(tone, HAZE_C, 0.35 + (z > h - 0.6 ? -0.2 : 0));
    });
  }
}
/** the station: the Swanston Street front and the low concourse beyond it, the corner, the drum and the dome, the façade */
let ST_DEPTH = null;                                  // the station's depth per column, for the light strings' occlusion
function paintStation(p, r) {
  // the concourse roofs along Swanston Street towards Princes Bridge, low and dark, lit along their eaves
  paintWall(p, RET.x, RET.y1, 0, -1, 45, 0, 6.5, (s, z, sx, sy, u) => z > 5.8 ? '#8a7a7a' : z > 5.2 ? '#2a2428' : z < 3.2 ? (frac(s / 4) < 0.6 ? pick(['#7a5a3a', '#c89a5a'], 0.5, sx, sy) : '#24202a') : pick(['#2a2630', '#34303a'], 0.5, sx, sy));
  const dRet = paintWall(p, RET.x, RET.y0, 0, -1, RET.y0 - RET.y1, 0, RET.h, frontMat('S', RET.y0 - RET.y1, RET.h));
  const dFac = paintWall(p, FAC.x0, FAC.y, -1, 0, FAC.x0 - FAC.x1, 0, FAC.h, frontMat('F', FAC.x0 - FAC.x1, FAC.h));
  paintDome(p);
  const dCor = paintWall(p, ST.ax, ST.ay, -Math.SQRT1_2, Math.SQRT1_2, ST.len, 0, ST.top + 1.7, cornerMat);
  ST_DEPTH = { ...dRet, ...dFac, ...dCor };
  // the row of clocks: nine faces over the doors (painted per frame)
  for (let k = 0; k < 9; k++) {
    const s = ARCH.sc + (k - 4) * 0.92, [x, y] = proj(ST.ax - Math.SQRT1_2 * s, ST.ay + Math.SQRT1_2 * s, (ARCH.clock0 + ARCH.clock1) / 2 - 0.1);
    CLOCKS.push([Math.round(x), Math.round(y)]);
  }
}
/* ================= The station's lights: bulbs along its cornices, arches, the dome's ribs and the tower ================= */
const BULBS = [];                                     // [x, y, k, phase]: each shimmers gently on its own
function addBulb(x, y, set) {
  x = Math.round(x); y = Math.round(y);
  const key = x * 1000 + y;
  if (set.has(key) || x < 0 || x >= W || y < 0) return;
  set.add(key); BULBS.push([x, y, 3 + Math.floor(hash(x, y, 5) * 9), hash(x, y, 6)]);
}
function layBulbs() {
  const seen = new Set(), every = (n, f) => { for (let i = 0; i <= n; i++) f(i / n); };
  const along3 = (A, B, step) => { const n = Math.ceil(Math.hypot(B[0] - A[0], B[1] - A[1], B[2] - A[2]) / step); every(n, u => { const q = proj(lerp(A[0], B[0], u), lerp(A[1], B[1], u), lerp(A[2], B[2], u)); addBulb(q[0], q[1], seen); }); };
  along3([FAC.x0, FAC.y + 0.1, 16.05], [FAC.x1, FAC.y + 0.1, 16.05], 0.7);           // the façade's cornice
  along3([FAC.x0, FAC.y + 0.1, FAC.h], [FAC.x1, FAC.y + 0.1, FAC.h], 1.0);           // its parapet
  along3([RET.x + 0.1, RET.y0, 16.05], [RET.x + 0.1, RET.y1, 16.05], 0.7);
  const cx = s => ST.ax - Math.SQRT1_2 * s, cy = s => ST.ay + Math.SQRT1_2 * s;
  along3([cx(0), cy(0), 16.6], [cx(ST.len), cy(ST.len), 16.6], 0.5);                  // the corner's cornice
  for (let a = 0; a <= 36; a++) {                                                     // the great arch
    const ang = Math.PI * a / 36, s = ARCH.sc + Math.cos(ang) * (ARCH.half + ARCH.ring), z = ARCH.spring + Math.sin(ang) * (ARCH.half + ARCH.ring);
    const q = proj(cx(s) + 0.2, cy(s) + 0.2, z); addBulb(q[0], q[1], seen);
  }
  for (let rib = -2; rib <= 2; rib++) {                                               // the dome's ribs, as we see them
    const phi = rib * Math.PI / 6 - 0.12;
    for (let z = DOME.z0 + 0.3; z < DOME.z1 - 0.2; z += 0.45) {
      const [ax, , u] = proj(DOME.x, DOME.y, z), y = rowOf(z, u), r = domeR(z);
      addBulb(ax + Math.sin(phi) * r * FL / u, y, seen);
    }
  }
  for (const [x, y] of [[TOWER.x1, TOWER.y1 + 0.5], [TOWER.x1, TOWER.y0]]) along3([x + 0.1, y, 3], [x + 0.1, y, TOWER.shaft], 1.4);   // the tower's corners
}

/* ================= Federation Square's corner: walls tiled in triangles of sandstone, zinc and glass ================= */
const FED_ROOF = s => Math.min(15.5, 11 + s * 0.45);
let FED_DEPTH = {}, YJ_DEPTH = {};
function fedMat(s, z, sx, sy, u) {
  if (z > FED_ROOF(s)) return null;
  if (z > FED_ROOF(s) - 0.35) return '#9aa2b8';                                      // the lit edge of the roof's slope
  if (z < 4) {                                                                       // the bar on the corner, glazed, warm inside
    if (z > 3.6) return '#2a2a36';
    const mull = frac(s / 1.8) < 0.08;
    return mull ? '#1a1a24' : pick(['#6a4630', '#b07a48', '#e8b070', '#ffd89a'], 0.25 + 0.55 * (1 - z / 4) + 0.15 * Math.sin(s * 1.3), sx, sy);
  }
  // the pinwheel grid: triangles 1.6 m on a side, each a panel of one material
  const T = 1.6, b = (z - 4) / (T * 0.866), row = Math.floor(b), a = s / T + (row & 1) * 0.5, col = Math.floor(a);
  const fa = a - col, fb = b - row, up = fa > fb * 0.5 && fa < 1 - fb * 0.5 ? 0 : 1, cell = hash(col * 2 + up, row, 17);
  const edge = fb < 0.06 || Math.abs(fa - fb * 0.5) < 0.04 || Math.abs(fa - (1 - fb * 0.5)) < 0.04;
  if (edge) return '#22222e';
  const wash = 0.5 + 0.25 * Math.exp(-(z - 4) / 5);                                  // the uplights at its foot
  // each big triangle splits into smaller ones (the pinwheel); the small ones vary in shade within their panel
  const sub = hash(Math.floor(fa * 3), Math.floor(fb * 2) + col * 7, row * 13 + 5) * 0.16;
  if (cell < 0.36) return pick(['#4a3e34', '#64544a', '#7e6a56', '#98805e'], wash - sub, sx, sy);   // sandstone
  if (cell < 0.76) return pick(['#2c303c', '#3a404e', '#4c5464', '#626c80'], wash - 0.08 - sub, sx, sy);   // zinc
  if (cell < 0.84) return (sx + sy) % 2 ? '#2c303c' : '#454c5c';                      // perforated zinc
  return cell < 0.9 ? pick(['#a07848', '#d0a46a'], wash, sx, sy) : pick(['#101422', '#161c2e'], 0.5, sx, sy);   // glass, lit or dark
}

/* ================= Young & Jackson's, the hotel on the north-west corner ================= */
function yjMat(s, z, sx, sy, u) {
  const lt = 0.3 + 0.32 * Math.exp(-z / 3.5) + 0.06 * smooth(14, 16, z) * (z < 16.2 ? 1 : 0);
  const STONE = ['#2a2428', '#3a3234', '#4e4442', '#64584e', '#7c6c5c', '#968470'];
  if (z > YJ.h - 0.4) return pick(STONE, lt + 0.2, sx, sy);
  if (z > 15.2) return frac(s / 0.9) < 0.35 && z < YJ.h - 0.5 && z > 15.6 ? '#2a2226' : pick(STONE, lt + 0.05, sx, sy);   // the balustrade
  if (z > 14.6) return z < 14.8 ? '#2a2024' : pick(STONE, lt + 0.15, sx, sy);
  const bay = Math.floor((s - 0.6) / 3.1), bs = s - 0.6 - bay * 3.1;
  if (z < 4.4) {                                                                     // the bar: tall windows, warm inside
    if (z > 3.9) return pick(['#2a1e1a', '#3a2a22'], 0.5, sx, sy);
    if (bs > 0.35 && bs < 2.75 && z > 0.7) return frac(bs / 0.8) < 0.1 || Math.abs(z - 2.9) < 0.08 ? '#3a241a' : pick(['#a8683a', '#e09a52', '#ffc87a', '#ffe2a8'], 0.3 + 0.5 * (1 - z / 4) + 0.2 * (s < 4 ? 1 : 0), sx, sy);
    return pick(['#2a2226', '#3a3034', '#4e4246'], 0.5, sx, sy);
  }
  if (z < 5.1) return z < 4.6 ? '#fbe0a8' : pick(['#1e1618', '#2a2024'], 0.5, sx, sy);   // the verandah over the footpath
  for (const [f, b0, t0] of [[0, 6.2, 9.4], [1, 10.4, 13.6]]) {
    if (bs > 0.7 && bs < 2.4 && ((z > t0 + 0.1 && z < t0 + 0.5) || (z > b0 - 0.35 && z < b0 - 0.1))) return pick(STONE, lt + 0.22, sx, sy);   // hood, sill
    if (bs > 0.9 && bs < 2.2 && z > b0 && z < t0) {
      winPix(50000 + bay * 10 + f, sx, sy, pick(['#b8783e', '#d89a58', '#f0c27c'], 0.5 - (z - b0) * 0.06, sx, sy), keyHash);
      return pick(['#16121c', '#201a24'], 0.5, sx, sy);
    }
  }
  if (bs < 0.4 || bs > 2.7) return pick(STONE, lt + 0.08, sx, sy);                   // pilasters
  return pick(STONE, lt - 0.05 + (vnoise(s, z * 2, 5) - 0.5) * 0.1, sx, sy);
}
/* towers of the city behind the hotel, their tops showing over its roof */
function paintCity(p, r) {
  for (const [x0, x1, top] of [[330, 346, 70], [346, 362, 56], [362, 380, 64], [452, 466, 44], [466, 480, 30]]) {
    for (let y = top; y < 140; y++) for (let x = x0; x < x1; x++)
      p.set(x, y, x === x0 ? '#121426' : y === top ? '#34344c' : pick(['#16182c', '#1c1e34', '#23253c'], 0.35 + 0.3 * smooth(top, 140, y) + (x - x0) * 0.01, x, y));
    for (let y = top + 3; y < 132; y += 3) for (let x = x0 + 2; x < x1 - 1; x += 2) if (r() < 0.38) addWin(r, x, y, 1, 1, { pOn: 0.4, cool: r() < 0.4 });
  }
}

/* ================= The rooftop sign on Young & Jackson's (the city's billboard): a flat white ================= */
const STEEL = { d: '#0c0d18', m: '#181a28', h: '#2c3046', l: '#565c7a' };
function renderBoard() {
  const p = pix(W, H), L = BB.x - 2, Rr = BB.x + BB.w + 1, T0 = BB.y - 2, B = BB.y + BB.h + 1;
  const roofAt = sx => { const s = onWall(sx + 0.5, YJ.x, YJ.y0, 0, 1); return Math.round(rowOf(YJ.h, depthOf(YJ.x, YJ.y0 + s))); };
  for (const lx of [BB.x + 5, BB.x + 26, BB.x + 47]) {                               // legs down to the roof, braced
    const rf = roofAt(lx);
    for (let y = B; y <= rf; y++) { p.set(lx, y, STEEL.m); p.set(lx + 1, y, STEEL.d); }
  }
  p.line(BB.x + 6, B + 1, BB.x + 26, roofAt(BB.x + 26) - 1, STEEL.d); p.line(BB.x + 27, B + 1, BB.x + 47, roofAt(BB.x + 47) - 1, STEEL.d);
  for (let x = L; x <= Rr; x++) p.set(x, B + 3, STEEL.h);                             // a catwalk
  for (let y = T0; y <= B; y++) for (let x = L; x <= Rr; x++) {
    let col = STEEL.m;
    if (y === T0) col = '#9aa2bc'; else if (y === T0 + 1 || x === L) col = STEEL.h; else if (y === B || x === Rr) col = STEEL.d;
    p.set(x, y, col);
  }
  for (const lx of [BB.x + 12, BB.x + 41]) { p.line(lx, B + 3, lx, T0 - 4, STEEL.d); p.set(lx - 1, T0 - 4, STEEL.h); p.set(lx - 2, T0 - 3, '#fff2d0'); p.set(lx - 1, T0 - 3, '#fff2d0'); }   // two lamps over the face
  return trim(p);
}
/** the ad's lettering: the engine's 3x5 font, with a wide W so it can't be read as an H */
const WIDE_W = ['10001', '10001', '10101', '10101', '01010'];
function adText(c, s, x, y, col) {
  c.fillStyle = col;
  for (const ch of s) {
    const g = ch === 'W' ? WIDE_W : FONT3[ch];
    if (g) g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') c.fillRect(x + i, y + j, 1, 1); });
    x += (ch === 'W' ? 5 : 3) + 1;
  }
}
/** the ad: a green panel, a flat white in its cup with a heart in the foam, the words, the café's name */
function renderAd(steam) {
  const { w, h } = BB, p = pix(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) p.set(i, j, i === 0 || j === 0 || i === w - 1 || j === h - 1 ? '#3a5a44' : pick(['#16301f', '#1c3a28', '#224430'], 0.5 + 0.3 * (1 - j / h), i, j));
  const cx = 8;
  for (let i = -6; i <= 6; i++) { p.set(cx + i, 19, Math.abs(i) > 5 ? '#b8b4aa' : '#e8e2d6'); if (Math.abs(i) < 5) p.set(cx + i, 20, '#9a968c'); }   // the saucer
  for (let j = 13; j <= 18; j++) { const hw = Math.round(5 - (j - 13) * 0.5); for (let i = -hw; i <= hw; i++) p.set(cx + i, j, i === hw ? '#b8b2a6' : '#f2ede2'); }   // the cup
  for (const j of [14, 15, 16]) p.set(cx + 6, j, '#e8e2d6');                          // its handle
  p.set(cx + 5, 13, '#e8e2d6'); p.set(cx + 5, 17, '#e8e2d6');
  for (let i = -5; i <= 5; i++) { p.set(cx + i, 10, '#f2ede2'); for (const j of [11, 12]) p.set(cx + i, j, Math.abs(i) < 5 ? '#a8703e' : '#f2ede2'); }   // the rim, the coffee
  for (const [i, j] of [[-1, 11], [1, 11], [0, 12]]) p.set(cx + i, j, '#f4e6cc');    // a heart in the foam
  const S = [[[-2, 9], [-1, 8]], [[1, 9], [2, 8]], [[-1, 9], [0, 8]]];
  for (const [i, j] of S[steam]) p.set(cx + i, j, '#d8d4cc');
  p.done();
  adText(p.x, 'FLAT WHITE', 7, 2, '#f6ecd6'); adText(p.x, 'GULL CAFE', 17, 12, '#e8b44c');
  p.d.set(p.x.getImageData(0, 0, w, h).data);
  return p.done();
}

/* ================= The ground: wet asphalt, bluestone kerbs and footpaths, St Paul's forecourt ================= */
const ROAD = ['#0b0c18', '#0f1020', '#141628', '#191b2f', '#202238', '#282a42'];
const BLUE = ['#121320', '#181926', '#1e1f2e', '#262738', '#2f3044', '#3a3a52'];   // wet bluestone
const SAND = ['#272220', '#352e2a', '#463c34', '#584c40', '#6c5e4e'];           // the forecourt's paving
const LAMPS = [{ x: 11.6, y: 22.5, z: 5.4 }, { x: 24, y: 10.7, z: 5.4 }];          // the two lamps by St Paul's
const zoneOf = (x, y) => (Math.abs(y) <= KERB || Math.abs(x) <= KERB ? 'road'
  : Math.abs(y) <= LOT || Math.abs(x) <= LOT ? 'foot' : x > LOT && y > LOT ? 'fore' : x > LOT ? 'fed' : 'lot');
/** warm light lying on the ground: the floodlit station, the lamps, the hotel's bar, Fed Square's bar */
function groundLight(x, y) {
  let v = 0;
  if (x < -12 && y < -8) v += 0.55 * Math.exp(-Math.max(0, y + 15) / 3.5);                   // under the station's façade
  if (y < -12 && x < -8 && x > -16) v += 0.5 * Math.exp(-Math.max(0, x + 15) / 3);         // its Swanston front
  if (Math.hypot(x + 21, y + 21) < 12) v += 0.4 * (1 - Math.hypot(x + 21, y + 21) / 12);   // the steps under the clocks
  if (x < -9 && y > 12) v += 0.4 * Math.exp(-Math.max(0, x + 15) / 3);                    // Young & Jackson's bar windows
  if (y < -9 && x > 12) v += 0.35 * Math.exp(-Math.max(0, -y - 15) / 3);                  // the bar at Fed Square's corner
  for (const L of LAMPS) v += 0.55 * Math.exp(-((x - L.x) ** 2 + (y - L.y) ** 2) / 30);
  return Math.min(1, v);
}
function renderGround() {
  const p = pix(W, H);
  for (let sy = HZ + 1; sy < H; sy++) for (let sx = 0; sx < W; sx++) {
    const [x, y, u] = groundAt(sx + 0.5, sy + 0.5), zone = zoneOf(x, y), g = groundLight(x, y);
    const n = (vnoise(x / 3, y / 3, 41) - 0.5) * 0.2 + (hash(Math.floor(x * 3), Math.floor(y * 3), 42) - 0.5) * 0.1 * Math.min(1, 20 / u);
    let col;
    if (zone === 'road') col = pick(ROAD, 0.35 + n + 0.12 * Math.min(1, 15 / u) + g * 0.5, sx, sy);
    else if (zone === 'fore' || zone === 'fed') {                                    // sandstone slabs, wet
      const joint = u < 30 && (frac(x / 1.2) < 0.06 || frac(y / 1.2) < 0.06);
      col = joint ? '#14121a' : pick(SAND, 0.3 + n + g * 0.6, sx, sy);
    } else {                                                                         // bluestone pavers
      const joint = u < 25 && (frac(x / 0.6 + (Math.floor(y / 0.3) & 1) * 0.5) < 0.08 || frac(y / 0.3) < 0.1);
      col = joint ? '#0e0e18' : pick(BLUE, 0.3 + n + g * 0.7, sx, sy);
      if (g > 0.25 && !joint && bayer(sx, sy) < g * 0.5) col = mixHex(col, '#c89060', 0.25);   // the warm light on wet stone
    }
    const pd = PUDDLES.find(([cx, cy, rx, ry]) => ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 < 1);
    if (pd) col = ((x - pd[0]) / pd[2]) ** 2 + ((y - pd[1]) / pd[3]) ** 2 > 0.72 && y > pd[1] ? '#4a4a64' : pick(['#1a1c32', '#24263e', '#30304a', '#5a4a4a'], 0.35 + g * 0.8 + (vnoise(x * 2, y * 4, 9) - 0.5) * 0.3, sx, sy);
    p.set(sx, sy, col);
  }
  return p.done();
}
const PUDDLES = [[16.6, 13.6, 1.1, 0.5], [-3, 6.5, 2.6, 1.1], [5.5, -4.5, 2.2, 0.9], [-6, -7.5, 1.8, 0.8]];
/* the marks on the road: rails, the crossings' white lines, lane dashes, kerbs, the platforms' edges, the forecourt's step */
const RAILS = [TRK - GAUGE, TRK + GAUGE, -TRK - GAUGE, -TRK + GAUGE];
function renderMarks() {
  const p = pix(W, H);
  for (let sy = HZ + 1; sy < H; sy++) for (let sx = 0; sx < W; sx++) {
    const c = [groundAt(sx, sy), groundAt(sx + 1, sy), groundAt(sx, sy + 1), groundAt(sx + 1, sy + 1)];
    const x0 = Math.min(...c.map(q => q[0])), x1 = Math.max(...c.map(q => q[0])), y0 = Math.min(...c.map(q => q[1])), y1 = Math.max(...c.map(q => q[1]));
    const [x, y, u] = groundAt(sx + 0.5, sy + 0.5);
    if (zat(sx, sy) < u) continue;                                                   // behind a building
    const g = groundLight(x, y), zone = zoneOf(x, y);
    // how much of the pixel a line at v (half-width hw) across the span [a, b] covers
    const cov = (a, b, v, hw) => (a <= v + hw && b >= v - hw ? Math.min(1, (2 * hw + 0.02) / Math.max(0.02, b - a)) : 0);
    let best = 0, col = null;
    const take = (a, c2) => { if (a > best) { best = a; col = c2; } };
    if (zone === 'road') {
      for (const v of RAILS) {
        take(cov(y0, y1, v, 0.05), g > 0.2 ? '#9a96a8' : '#5c5e74');                 // Flinders Street's rails
        take(cov(x0, x1, v, 0.05), g > 0.2 ? '#9a96a8' : '#5c5e74');                 // Swanston Street's
      }
      const paint = hash(Math.floor(x * 2), Math.floor(y * 2), 51) < 0.14 ? '#5a5c6c' : g > 0.2 ? '#d8d4cc' : '#a8acbc';
      if (Math.abs(x) <= KERB) for (const v of [11.15, 14.15, -11.15, -14.15]) take(cov(y0, y1, v, 0.16), paint);   // across Swanston
      if (Math.abs(y) <= KERB) for (const v of [11.15, 14.15, -11.15, -14.15]) take(cov(x0, x1, v, 0.16), paint);   // across Flinders
      if (Math.abs(x) > 17 && frac(x / 6) < 0.45) for (const v of [4.6, -4.6]) take(cov(y0, y1, v, 0.07), paint);
      if (Math.abs(y) > 17 && frac(y / 6) < 0.45) for (const v of [4.6, -4.6]) take(cov(x0, x1, v, 0.07), paint);
      for (const s of [1, -1]) {                                                     // gutters along the kerbs
        if (Math.abs(x) > KERB) take(cov(y0, y1, s * (KERB - 0.12), 0.12), '#06070e');
        if (Math.abs(y) > KERB) take(cov(x0, x1, s * (KERB - 0.12), 0.12), '#06070e');
      }
      if (x > PLAT.x0 && x < PLAT.x1 && y < -2.9 && y > -5.3) { best = 1; col = y > -3.25 ? '#c8a03a' : pick(['#3a3a48', '#4a4a5a'], 0.5, sx, sy); }   // the Flinders platform
      if (y > SPLAT.y0 && y < SPLAT.y1 && x < -2.9 && x > -5.3) { best = 1; col = x > -3.25 ? '#c8a03a' : pick(['#3a3a48', '#4c4c5e'], 0.5, sx, sy); }   // Swanston's
    } else {
      for (const s of [1, -1]) {                                                     // the kerbs' bluestone tops
        const kc = u < 14 ? '#3a3c4c' : g > 0.2 ? '#7a7484' : '#4a4c5e';
        if (Math.abs(x) > KERB) take(cov(y0, y1, s * (KERB + 0.15), 0.15), kc);
        if (Math.abs(y) > KERB) take(cov(x0, x1, s * (KERB + 0.15), 0.15), kc);
      }
      if (zone === 'fore') { take(cov(y0, y1, 16.1, 0.08), '#5e5446'); take(cov(x0, x1, 16.1, 0.08), '#5e5446'); }   // the forecourt's step
    }
    if (col && best > 0.12) p.set(sx, sy, col, Math.round(255 * Math.min(1, best * 1.2)));
  }
  return p.done();
}

/* ================= Overhead: the trams' wires, their poles; the signals ================= */
const POLES = [                                       // tram poles along the kerbs: [x, y]
  [-45, -10.9], [-75, -10.9], [-105, -10.9], [-45, 10.9], [-75, 10.9], [-10.9, -40], [-10.9, -72], [10.9, -72],
];
const SIGNALS = [                                     // signal poles at the corners: [x, y, kind of heads]
  { x: -18, y: -11, veh: 'fl', ped: 'walkF' }, { x: 11.5, y: -11.5, veh: 'sw', ped: 'walkS' }, { x: -11.5, y: 14, veh: 'fl', ped: 'walkS', tram: true },
];
function renderWires() {
  const p = pix(W, H), WIRE = '#262432', HI = 5.6, line3 = (A, B, sag = 0) => p.line3(A, B, WIRE, sag);
  for (const v of [TRK, -TRK]) {
    p.line3([-260, v, HI], [60, v, HI], WIRE);                                       // the contact wires over Flinders Street
    p.line3([v, -260, HI], [v, 40, HI], WIRE);                                       // and over Swanston Street
  }
  for (const [x] of POLES.filter(q => Math.abs(q[1]) > 10 && Math.abs(q[0]) > 20)) p.line3([x, -10.9, 6.6], [x, 10.9, 6.6], WIRE, 0.5);   // span wires
  for (const [, y] of POLES.filter(q => Math.abs(q[0]) < 12)) p.line3([-10.9, y, 6.6], [10.9, y, 6.6], WIRE, 0.5);
  for (const [a, b] of [[[-11, 11, 6.8], [11, -11, 6.8]]]) p.line3(a, b, WIRE, 0.6);   // a span across the crossing
  // thin the wires where they cross the lit station, so they read as wires and not as a fence
  for (let i = 3; i < p.d.length; i += 4) if (p.d[i]) p.d[i] = 150;
  return p.done();
}
/** a post from the ground to height z at (x, y): its column, its top row, its depth */
function postAt(x, y, z) { const [sx, sy0, u] = proj(x, y, 0), [, sy1] = proj(x, y, z); return { x: Math.round(sx), y0: Math.round(sy0), y1: Math.round(sy1), u }; }
function drawPost(P, col = '#1a1c26', hi = '#3a3c50') {
  const w = P.u < 25 ? 2 : 1;
  R(P.x, P.y1, w, P.y0 - P.y1 + 1, col);
  if (w > 1) R(P.x, P.y1, 1, P.y0 - P.y1 + 1, hi);
}
/** the signals' state at t: Flinders and Swanston traffic, and the walk signs on the crossings across each */
function signals(t) {
  const tau = t % SIG_T;
  return {
    fl: tau < 26 ? 'g' : tau < 29 ? 'a' : 'r',
    sw: tau >= 30 && tau < 56 ? 'g' : tau >= 56 && tau < 59 ? 'a' : 'r',
    walkS: tau < 10 ? 'go' : tau < 20 ? (frac(tau) < 0.5 ? 'red' : 'off') : 'red',  // crossing Swanston, with Flinders' traffic
    walkF: tau >= 30 && tau < 40 ? 'go' : tau >= 40 && tau < 50 ? (frac(tau) < 0.5 ? 'red' : 'off') : 'red',
  };
}
const SIG_COL = { r: '#ff3a2e', a: '#ffae2a', g: '#3ef0a6' };
function drawSignal(S, s) {
  const P = postAt(S.x, S.y, 4.6), k = Math.max(1, Math.round(5 / P.u * 6));     // k: the size of a lamp in px
  drawPost(P);
  const hx = P.x + 1, hy = P.y1 + 1, st = s[S.veh];
  R(hx, hy, k + 1, k * 3 + 2, '#0c0c14');                                             // the vehicle head: red, amber, green
  const li = st === 'r' ? 0 : st === 'a' ? 1 : 2;
  R(hx + (k > 1 ? 1 : 0), hy + 1 + li * k, k, k, SIG_COL[st]);
  halo(HL.sig[st], hx + 1, hy + 1 + li * k, 0.8);
  if (S.tram) {                                                                      // the trams' own signal: a white bar, upright to go
    const tx = P.x - k - 2, ty = hy + 1;
    R(tx, ty, k + 2, k + 2, '#0c0c14');
    if (s.sw === 'g') R(tx + (k >> 1) + 1, ty + 1, 1, k, '#f4f6ff'); else R(tx + 1, ty + (k >> 1) + 1, k, 1, '#f4f6ff');
  }
  const ped = s[S.ped], py = hy + k * 3 + 3;                                         // the walk sign: a red or a green figure
  R(hx, py, k + 1, k + 2, '#0c0c14');
  if (ped !== 'off') { R(hx + (k > 1 ? 1 : 0), py + 1, k, k, ped === 'go' ? '#7af6c8' : '#ff4a3a'); halo(ped === 'go' ? HL.sig.g : HL.sig.r, hx + 1, py + 1, 0.6); }
}
/** the platform shelters: Flinders Street's, by the station (far), and Swanston Street's, by the hotel (near) */
function renderShelters() {
  const p = pix(W, H), ROOFC = '#3a3c4c', GLASS = '#5a6a8a';
  const shelter = (ax, ay, bx, by, back) => {                                        // posts, a glass back, a flat roof, a lit panel
    p.line3([ax, ay, 2.7], [bx, by, 2.7], ROOFC); p.line3([ax, ay, 2.6], [bx, by, 2.6], '#8a8c9c');
    for (const f of [0, 0.5, 1]) { const x = lerp(ax, bx, f), y = lerp(ay, by, f); p.line3([x, y, 0.2], [x, y, 2.6], '#2a2c38'); }
    for (let f = 0.05; f < 0.95; f += 0.02) { const x = lerp(ax, bx, f) + back[0], y = lerp(ay, by, f) + back[1]; p.line3([x, y, 0.5], [x, y, 2.2], GLASS); }
    const x = lerp(ax, bx, 0.75) + back[0], y = lerp(ay, by, 0.75) + back[1];
    for (let z = 0.6; z < 2.2; z += 0.05) p.line3([x - Math.abs(back[1]) * 0.6, y - Math.abs(back[0]) * 0.6, z], [x + Math.abs(back[1]) * 0.6, y + Math.abs(back[0]) * 0.6, z], '#d8c8a0');
  };
  shelter(-36, -4.2, -24, -4.2, [0, -0.6]);
  shelter(-4.2, 24, -4.2, 36, [-0.6, 0]);
  return trim(p);
}

/* ================= Trams and cars: boxes in the world, painted column by column ================= */
/**
 * Paint a vertical face from (ax, ay) to (bx, by) column by column: band(s, sx, u) fills the column, s metres from a.
 * Columns behind a building (the depth buffer, read at the face's middle row) are skipped.
 */
function face(ax, ay, bx, by, zMid, band) {
  const len = Math.hypot(bx - ax, by - ay), Dx = (bx - ax) / len, Dy = (by - ay) / len;
  const pa = proj(ax, ay, 0), pb = proj(bx, by, 0);
  if (pa[2] < 2 && pb[2] < 2) return;
  const behind = pa[2] < 2 || pb[2] < 2;                                              // one end beside or behind us: scan every column
  const x0 = behind ? 0 : Math.max(0, Math.floor(Math.min(pa[0], pb[0]))), x1 = behind ? W - 1 : Math.min(W - 1, Math.ceil(Math.max(pa[0], pb[0])));
  for (let sx = x0; sx <= x1; sx++) {
    const s = onWall(sx + 0.5, ax, ay, Dx, Dy);
    if (s < 0 || s > len) continue;
    const u = depthOf(ax + Dx * s, ay + Dy * s);
    if (u < 2 || zat(sx, rowOf(zMid, u)) < u) continue;
    band(s, sx, u, len);
  }
}
/** fill rows of column sx between heights z0 and z1 at depth u (mir: its mirror image in the wet road) */
function zfill(sx, u, z0, z1, col, mir = false) {
  let ya = rowOf(mir ? -z0 : z1, u), yb = rowOf(mir ? -z1 : z0, u);
  ya = Math.round(ya); yb = Math.round(yb);
  if (yb < ya) return;
  ctx.fillStyle = col; ctx.fillRect(sx, ya, 1, yb - ya + 1);
}
/** a box standing on the road: centre (cx, cy), heading hd (radians, 0 = east), length L, width B, height Hz.
 *  side(name, s, sx, u, len, mir) paints a column of a face ('front', 'rear', 'left', 'right'); roof: colour or null */
function drawBox(cx, cy, hd, L, B, Hz, side, roof) {
  const fx = Math.cos(hd), fy = Math.sin(hd), nx = -fy, ny = fx, hl = L / 2, hb = B / 2;
  const P = (a, b) => [cx + fx * a + nx * b, cy + fy * a + ny * b];
  const c = [P(-hl, -hb), P(hl, -hb), P(hl, hb), P(-hl, hb)];                        // rear-right, front-right, front-left, rear-left
  const faces = [['right', c[0], c[1], -nx, -ny], ['front', c[1], c[2], fx, fy], ['left', c[2], c[3], nx, ny], ['rear', c[3], c[0], -fx, -fy]]
    .filter(([, a, b, ox, oy]) => ox * (VX - (a[0] + b[0]) / 2) + oy * (VY - (a[1] + b[1]) / 2) > 0)
    .sort((A, B2) => depthOf((B2[1][0] + B2[2][0]) / 2, (B2[1][1] + B2[2][1]) / 2) - depthOf((A[1][0] + A[2][0]) / 2, (A[1][1] + A[2][1]) / 2));
  ctx.globalAlpha = 0.2;                                                             // the mirror images in the wet road first
  for (const [name, a, b] of faces) face(a[0], a[1], b[0], b[1], 0.5, (s, sx, u, len) => side(name, s, sx, u, len, true));
  ctx.globalAlpha = 1;
  for (const [name, a, b] of faces) face(a[0], a[1], b[0], b[1], Hz / 2, (s, sx, u, len) => side(name, s, sx, u, len, false));
  if (roof && Hz < EYE) {                                                            // the roof, seen from above: fill its rows
    const q = c.map(([x, y]) => proj(x, y, Hz));
    if (q.some(v => v[2] < 2)) return;                                               // a corner beside or behind us: no roof to see
    const ys = q.map(v => v[1]), y0 = Math.ceil(Math.min(...ys)), y1 = Math.floor(Math.max(...ys));
    ctx.fillStyle = roof;
    for (let y = y0; y <= y1; y++) {
      let a = Infinity, b = -Infinity;
      for (let i = 0; i < 4; i++) {
        const p0 = q[i], p1 = q[(i + 1) % 4];
        if ((y - p0[1]) * (y - p1[1]) > 0 || p0[1] === p1[1]) continue;
        const x = lerp(p0[0], p1[0], (y - p0[1]) / (p1[1] - p0[1]));
        a = Math.min(a, x); b = Math.max(b, x);
      }
      if (b >= a) ctx.fillRect(Math.round(a), y, Math.round(b) - Math.round(a) + 1, 1);
    }
  }
}

/* the trams: a modern one, white and green; an older one, green below and white above; the City Circle's maroon W-class */
const LIVERY = {
  E: { len: 24, body: '#d6d8d8', bodyD: '#5a5e6a', band: ACCENT, lower: '#c8cacc', win: ['#e8eef8', '#8e9cb6'], roof: '#6a6e7c', dest: '#ffb43a', pitch: 1.7 },
  Z: { len: 16, body: '#dcdacc', bodyD: '#5a5a52', band: '#1e7a44', lower: '#1e7a44', win: ['#fff0c8', '#c8a46a'], roof: '#5e626e', dest: '#ffb43a', pitch: 1.4 },
  W: { len: 14.5, body: '#741c26', bodyD: '#3a0e14', band: '#d8b45a', lower: '#5a141c', win: ['#ffe2a0', '#d89a50'], roof: '#463630', dest: '#fff0c8', pitch: 0.95 },
};
/** a column of a tram's face; s from the face's start, len the face's length */
function tramSide(T, lead) {
  return (name, s, sx, u, len, mir) => {
    const L = LIVERY[T.kind], end = name === 'front' || name === 'rear';
    const isFront = end && (name === 'front') === lead;
    zfill(sx, u, 0.1, 0.55, '#16161e', mir);                                         // the bogies and skirt
    if (end) {
      zfill(sx, u, 0.55, 1.2, L.lower, mir);
      const glass = Math.abs(s - len / 2) < len / 2 - 0.25;
      zfill(sx, u, 1.2, 2.6, glass ? (isFront ? '#2a3448' : '#3a4458') : L.body, mir);
      zfill(sx, u, 2.6, 3.3, L.body, mir);
      if (!mir && Math.abs(s - len / 2) < 0.7) zfill(sx, u, 2.75, 3.05, L.dest);      // the destination sign
      if (!mir && (s < 0.5 || s > len - 0.5)) zfill(sx, u, 0.75, 0.95, isFront ? '#fff6dc' : '#ff3a2a');   // head or tail lamps
    } else {
      const sl = name === 'left' ? len - s : s;                                     // along the tram from its rear
      const door = [0.18, 0.5, 0.82].some(f => Math.abs(sl - f * len) < 0.65), win = Math.floor(sl / L.pitch);
      const pillar = frac(sl / L.pitch) < 0.16 || sl < 0.4 || sl > len - 0.4;
      zfill(sx, u, 0.55, 1.2, door ? '#2a2c34' : L.lower, mir);
      if (L.band !== L.lower) zfill(sx, u, 0.92, 1.1, L.band, mir);
      zfill(sx, u, 1.14, 1.2, L.bodyD, mir);
      if (pillar && !door) zfill(sx, u, 1.2, 2.6, L.body, mir);
      else {
        zfill(sx, u, 1.2, 2.6, L.win[1], mir);                                       // the lit inside, seats and people
        zfill(sx, u, 2.25, 2.6, L.win[0], mir);                                      // the ceiling lights
        if (!mir && !door && !T.empty && hash(win, T.seed, 3) < 0.45) zfill(sx, u, 1.3, 2.05, '#2a2a36');   // a passenger (none at crowd 0)
        if (door && !mir && frac(sl * 2) < 0.12) zfill(sx, u, 0.6, 2.6, '#1a1c24');
      }
      zfill(sx, u, 2.6, 3.15, L.body, mir);
      if (T.kind === 'W') { zfill(sx, u, 2.66, 2.72, L.band, mir); zfill(sx, u, 1.0, 1.06, L.band, mir); }
      zfill(sx, u, 3.15, 3.35, L.roof, mir);
    }
  };
}
/** a tram on its track at time t: axis 'F' (Flinders) or 'S' (Swanston), lane offset, the front's position, heading */
function drawTram(T, front, t) {
  const L = LIVERY[T.kind], half = L.len / 2, lane = T.lane, dir = T.dir;
  const c = front - dir * half, cx = T.axis === 'F' ? c : lane, cy = T.axis === 'F' ? lane : c;
  const hd = T.axis === 'F' ? (dir > 0 ? 0 : Math.PI) : (dir > 0 ? Math.PI / 2 : -Math.PI / 2);
  drawBox(cx, cy, hd, L.len, 2.65, 3.35, tramSide(T, true), null);
  // the pantograph up to the wire, its spark of light where it meets it
  const px = T.axis === 'F' ? c : lane, py = T.axis === 'F' ? lane : c, a = proj(px, py, 3.35), b = proj(px, py, 5.6);
  if (a[2] > 2 && zat(a[0], a[1]) >= a[2]) {
    ctx.fillStyle = '#3a3c48';
    for (let y = Math.round(b[1]); y <= Math.round(a[1]); y++) { const f = (y - b[1]) / Math.max(1, a[1] - b[1]); ctx.fillRect(Math.round(a[0] + (f < 0.5 ? f : 1 - f) * 6 * 8 / a[2]), y, 1, 1); }
  }
  // the lamps: headlamps at the front throw light on the road, the tail glows red
  const f2 = proj(T.axis === 'F' ? front : lane, T.axis === 'F' ? lane : front, 0.85), r2 = proj(T.axis === 'F' ? front - dir * L.len : lane, T.axis === 'F' ? lane : front - dir * L.len, 0.85);
  if (f2[2] > 1.5 && zat(f2[0], f2[1]) >= f2[2]) halo(HL.head, f2[0], f2[1], Math.min(1, 30 / f2[2]));
  if (r2[2] > 1.5 && zat(r2[0], r2[1]) >= r2[2]) halo(HL.tail, r2[0], r2[1], Math.min(0.8, 25 / r2[2]));
}
/* when each tram is where: keys [t, front position] (eased between keys) */
const TRAMS = [
  { id: 'F1', axis: 'F', lane: TRK, dir: -1, kind: 'E', seed: 1, keys: [[2, 34], [24, -132]] },
  { id: 'F2', axis: 'F', lane: -TRK, dir: 1, kind: 'Z', seed: 2, keys: [[180, -128], [210, 44]] },
  { id: 'CC', axis: 'F', lane: -TRK, dir: 1, kind: 'W', seed: 3, keys: [[CC.far, -128], [CC.stop0, -18], [CC.stop1, -18], [CC.out, 46]], cc: true },
  { id: 'S1', axis: 'S', lane: -TRK, dir: 1, kind: 'E', seed: 4, keys: [[32, -70], [46, 43], [54, 43], [60, 75]] },
  { id: 'S2', axis: 'S', lane: TRK, dir: -1, kind: 'Z', seed: 5, keys: [[90, 46], [108, -104]] },
  { id: 'S3', axis: 'S', lane: -TRK, dir: 1, kind: 'Z', seed: 6, keys: [[152, -70], [166, 43], [173, 43], [179, 75]] },
  { id: 'S4', axis: 'S', lane: TRK, dir: -1, kind: 'E', seed: 7, keys: [[210, 46], [228, -104]] },
];
const tramAt = (T, t) => (t < T.keys[0][0] || t >= T.keys[T.keys.length - 1][0] ? null : along(T.keys, t, false).v);
/** the City Circle's front between its keys: slowing into the stop, pulling away from it */
function ccFront(t) {
  if (t < CC.far || t >= CC.out) return null;
  if (t < CC.stop0) { const f = (t - CC.far) / (CC.stop0 - CC.far); return lerp(-128, -18, 1 - (1 - f) * (1 - f)); }
  if (t < CC.stop1) return -18;
  const f = (t - CC.stop1) / (CC.out - CC.stop1); return lerp(-18, 46, f * f * (3 - 2 * f) * 0.6 + f * f * 0.4);
}

/* cars along Flinders Street, and one making a hook turn into Swanston Street */
const CARS = [
  { id: 'taxi', col: '#e8c040', colD: '#a8862a', keys: [[4, 40, 6.6, Math.PI], [20, -140, 6.6, Math.PI]] },
  { id: 'ute', col: '#d8dade', colD: '#8a8e98', keys: [[124, 40, 6.6, Math.PI], [140, -140, 6.6, Math.PI]] },
  { id: 'hook', col: '#3a4a6a', colD: '#222c44', keys: [[64, -140, -6.6, 0], [72, -10, -6.6, 0], [74, 6.5, -7.6, 0], [91, 6.5, -7.6, 0], [94, 6.8, -12, -Math.PI / 2], [100, 6.6, -70, -Math.PI / 2]] },
];
function carAt(Cr, t) {
  const K = Cr.keys;
  if (t < K[0][0] || t >= K[K.length - 1][0]) return null;
  for (let i = 1; i < K.length; i++) if (t < K[i][0]) {
    const a = K[i - 1], b = K[i], f = (t - a[0]) / (b[0] - a[0]);
    let dh = b[3] - a[3];
    return { x: lerp(a[1], b[1], f), y: lerp(a[2], b[2], f), hd: a[3] + dh * f, stop: a[1] === b[1] && a[2] === b[2] };
  }
  return null;
}
function carSide(Cr, hook, t) {
  return (name, s, sx, u, len, mir) => {
    zfill(sx, u, 0.05, 0.6, '#101016', mir);                                        // tyres and the sill
    if (name === 'front' || name === 'rear') {
      zfill(sx, u, 0.3, 0.85, Cr.col, mir);
      zfill(sx, u, 0.85, 1.35, name === 'front' ? '#2a3040' : '#3a4050', mir);
      if (!mir && (s < 0.35 || s > len - 0.35)) zfill(sx, u, 0.5, 0.7, name === 'front' ? '#fff4d8' : (hook && t >= 73 && t < 91 ? '#ffb030' : '#ff3a2a'));
    } else {
      const wheel = Math.abs(s - 0.9) < 0.35 || Math.abs(s - (len - 0.9)) < 0.35;
      zfill(sx, u, 0.15, 0.85, wheel ? '#14141a' : Cr.col, mir);
      zfill(sx, u, 0.85, 1.32, s > 1 && s < len - 1.2 ? '#2a3040' : Cr.colD, mir);
      if (Cr.id === 'taxi' && !mir && Math.abs(s - len / 2) < 0.3) zfill(sx, u, 1.4, 1.55, '#fff2c0');   // the taxi's roof light
    }
  };
}
function drawCar(Cr, t) {
  const c = carAt(Cr, t);
  if (!c) return;
  drawBox(c.x, c.y, c.hd, 4.5, 1.8, 1.42, carSide(Cr, Cr.id === 'hook', t), Cr.col);
  const fwd = [c.x + Math.cos(c.hd) * 2.3, c.y + Math.sin(c.hd) * 2.3], back = [c.x - Math.cos(c.hd) * 2.3, c.y - Math.sin(c.hd) * 2.3];
  const f = proj(fwd[0], fwd[1], 0.6), b = proj(back[0], back[1], 0.6);
  if (f[2] > 2 && zat(f[0], f[1]) >= f[2]) halo(HL.head, f[0], f[1], Math.min(0.9, 22 / f[2]));
  if (b[2] > 2 && zat(b[0], b[1]) >= b[2]) halo(HL.tail, b[0], b[1], Math.min(0.7, 18 / b[2]));
  if (Cr.id === 'hook' && t >= 73 && t < 91 && frac(t * 1.5) < 0.5 && b[2] > 2) halo(HL.amber, b[0], b[1], 0.6);   // indicating right, waiting
}

/* ================= People ================= */
// One function draws everyone (after rome.js and stockholm.js): a look gives the proportions for a height and the
// colours dimmed for the night; person() puts it on its feet. Melbourne in the rain: black coats (the city's own
// uniform), a camel trench, an olive parka, one yellow raincoat; umbrellas, more of them up when the shower comes.
// Shoulders and umbrella tops catch the station's warm light. Each is drawn again upside down, faint, in the wet road.
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#cfcac4'];
const TOPS = ['#18181e', '#202028', '#2a2e3a', '#38363e', '#8a6a46', '#4a5638', '#c8a030', '#8a2a2e', '#283a5c', '#5a4a58', '#1c1c22', '#6a6a74'];
const BOTS = ['#121216', '#1c1e26', '#2a2a34', '#3a3440', '#46464e', '#24242c'];
const UMBS = ['#16161c', '#1e2a4e', '#a02a30', '#245e3e', '#a8946a', '#d0aa2c', '#16161c', '#3a3a46'];
const NIGHT = '#0c0e22', GLOWC = '#ffcf90';
const LOOKS = {};
function look(h, li) {
  const key = h * 100 + li;
  if (LOOKS[key]) return LOOKS[key];
  const q = (k, n) => Math.floor(hash(li, k, 911) * n), dk = 0.3;
  const m = c => mixHex(c, NIGHT, dk), top = TOPS[q(1, TOPS.length)], hair = HAIRS[q(2, HAIRS.length)];
  const hh = h >= 22 ? 5 : h >= 15 ? 4 : 3, th = Math.round(h * 0.38);
  return (LOOKS[key] = { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 24 ? 7 : h >= 18 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[q(3, 4)]), hair: m(hair), hairL: mixHex(hair, GLOWC, 0.3), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, GLOWC, 0.32), bot: m(BOTS[q(4, BOTS.length)]), shoe: m('#18141a'), long: q(5, 3) === 0 ? 2 + q(6, 3) : 0,
    coat: q(7, 2) ? 2 : 0, hat: q(8, 5) === 0 ? m(['#2a2a34', '#8a3a2a', '#d8d0c0'][q(9, 3)]) : null,
    umb: UMBS[q(10, UMBS.length)], umbP: hash(li, 11, 911) });
}
/**
 * One person, feet on row fy, centred on column x; returns the top row of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame;
 * o.aL / o.aR: 0 down, 2 bent up (a phone, a cup, an umbrella's handle), 3 out; o.umb: an umbrella (0..1 open);
 * o.phone: its screen glows
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2, legs = P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1);
  const ly = fy - legs + 1;
  if (side) {
    if (o.step) {
      R(x - 2, ly, 4, 2, P.bot);
      R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, P.bot); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
      R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, P.bot); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
    } else { R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
  } else {
    const lw = bw >= 7 ? 3 : 2, sh = o.step ? 1 : 0;
    R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2 - sh, P.bot); R(tx + bw - lw, ly + 2, lw, legs - 2, P.bot);
    R(tx, fy - sh, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
  }
  R(tx, ty, bw, th + P.coat, P.top);
  R(side ? (dir > 0 ? tx : tx + bw - 1) : tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    else { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
  };
  if (side) {
    const st = o.aR || 0, ax = x + (o.step ? -dir : 0);
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
    else { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) { R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL); if (P.long) R(hx, hy + hh, hw, P.long, P.hair); }
  else if (view === 1) { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); } }
  else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) R(hx, hy - 1, hw, 2, P.hat);
  if (o.phone) { const px = side ? x + dir * 2 : tx + bw; R(px, hy + hh, 1, 1, '#a8d8ff'); }
  if (o.umb) {                                                                       // the umbrella, opening if o.umb < 1
    const cw = Math.max(5, Math.round(P.h * 0.72 * (0.45 + 0.55 * o.umb))) | 1, ch = Math.max(2, Math.round(cw / 3.4 * (0.5 + 0.5 * o.umb)));
    const cx = side ? x + dir : x + 1, top = hy - 2 - ch;
    R(cx, top + ch, 1, hy - top - ch + 2, '#2a2a30');                                 // its stick
    for (let j = 0; j < ch; j++) {
      const half = Math.round((cw >> 1) * Math.sqrt((j + 1) / ch));
      R(cx - half, top + j, half * 2 + 1, 1, j === 0 ? mixHex(P.umb, GLOWC, 0.35) : j === ch - 1 ? mixHex(P.umb, NIGHT, 0.35) : P.umb);
    }
    R(cx, top - 1, 1, 1, '#3a3a40');
  }
  return hy;
}
/** someone far off, under 12 px: a stroke of a coat, a head, perhaps an umbrella */
function tiny(x, fy, h, li, umb, stp) {
  x = Math.round(x); fy = Math.round(fy);
  const P = look(12, li), head = h > 7 ? 2 : 1, body = Math.max(2, Math.round(h * 0.42)), w = h > 7 ? 2 : 1;
  R(x, fy - h + 1, 1, head, P.skin); R(x + w - 1, fy - h + 1, 1, 1, P.hair);
  R(x - (w > 1 ? 0 : 0), fy - h + 1 + head, w, body, P.top);
  const lg = h - head - body;
  if (lg > 0) { R(x + (stp ? 1 : 0) * (w - 1), fy - lg + 1, 1, lg, P.bot); if (w > 1) R(x + (stp ? 0 : 1), fy - lg + 1, 1, lg, P.bot); }
  if (umb) { R(x - 2, fy - h - 1, w + 4, 1, P.umb); R(x - 1, fy - h - 2, w + 2, 1, mixHex(P.umb, GLOWC, 0.3)); }
}
/** a person at world (x, y), standing on height z: draws them at their size, with their mirror image in the wet road */
function personAt(wx, wy, z, li, o, mirror = true) {
  const [sx, , u] = proj(wx, wy, 0), fy = rowOf(z, u), h = Math.round(FL * 1.8 / u);
  if (u < 2 || sx < -20 || sx > W + 20 || zat(sx, fy - h / 2) < u) return;
  const draw = () => { if (h >= 12) person(sx, fy, look(h, li), o); else tiny(sx, fy, Math.max(3, h), li, o.umb > 0.5, o.step); };
  if (mirror && z < 0.2) {
    ctx.save(); ctx.globalAlpha = 0.18; ctx.setTransform(1, 0, 0, -1, 0, 2 * Math.round(fy) + 2); draw(); ctx.restore();
  }
  draw();
}
const STRIDE = t => step(t, 768) & 1;                 // 3.2 steps a second
/** how open someone's umbrella is: their own share of the drizzle's umbrellas, all of them in the shower */
function umbOf(li, t) {
  const base = hash(li, 21, 5) < 0.5 ? 1 : 0;
  if (base) return 1;
  const opensAt = SHOWER.a + 2 + hash(li, 22, 5) * 12, closesAt = SHOWER.c + 4 + hash(li, 23, 5) * 10;
  if (t < opensAt || t >= closesAt) return 0;
  return Math.min(1, (t - opensAt) * 2.5, (closesAt - t) * 2.5);
}

/* ================= The crossings: people wait, the sign turns green, they cross, they walk on ================= */
// Each crossing: the second of the 60 s cycle its walk sign turns on; path: where they come from, where they wait, the
// far kerb, where they go. Everyone in a batch has a fixed slot (and rank); each of the four minutes of the loop
// brings different people (hash of the minute). via: a point they pass on the way in and on the way out (null: none),
// so the two crossings from and to St Paul's forecourt go round the coffee cart, between it and the couple.
const XINGS = [
  { id: 1, walk: 0, path: [[16.5, 23], [11.3, 12.4], [-11.3, 12.4], [-12.6, 42]], via: [[16.7, 17.4], null], sp: [0, 1], n: 5 },   // ours → the hotel
  { id: 2, walk: 0, path: [[-12.6, 36], [-11.3, 13.6], [11.3, 13.6], [18, 27]], via: [null, [17.2, 16.8]], sp: [0, 1], n: 4 },     // the hotel → ours
  { id: 3, walk: 30, path: [[22, 13.2], [12.4, 11.3], [12.4, -11.3], [32, -12.6]], sp: [1, 0], n: 5 },          // ours → Fed Square
  { id: 4, walk: 30, path: [[32, -12.8], [13.6, -11.3], [13.6, 11.3], [21, 18]], sp: [1, 0], n: 4 },            // Fed Square → ours
  { id: 5, walk: 30, path: [[-20.5, -20.5], [-12.4, -11.3], [-12.4, 11.3], [-12.6, 34]], sp: [1, 0], n: 4 },    // out of the station → the hotel
  { id: 6, walk: 30, path: [[-12.6, 36], [-13.6, 11.3], [-13.6, -11.3], [-20.5, -20.5]], sp: [1, 0], n: 3 },     // the hotel → into the station
  { id: 7, walk: 0, path: [[-20.5, -20.5], [-11.3, -12.4], [11.3, -12.4], [13.2, -40]], sp: [0, 1], n: 4 },     // the station → Fed Square
  { id: 8, walk: 0, path: [[13.4, -38], [11.3, -13.6], [-11.3, -13.6], [-20.5, -20.5]], sp: [0, 1], n: 3 },      // Fed Square → into the station
];
const DOORS = [-20.5, -20.5];
XINGS.forEach(X => {
  X.slots = [];
  for (let i = 0; i < X.n; i++) X.slots.push({ i, rank: X.id === 1 && i === 2 ? 0 : rankOf(X.id * 7 + i, 0.13), off: (i - (X.n - 1) / 2) * 0.75 });
});
const dist2 = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]);
const seg = (P0, P1, f) => [lerp(P0[0], P1[0], f), lerp(P0[1], P1[1], f), true, P1[0] - P0[0], P1[1] - P0[1]];
const polyLen = P => P.reduce((s, p, i) => i ? s + dist2(P[i - 1], p) : 0, 0);
/** the point a share f of the way along the line through the points P: [x, y, walking, dx, dy] */
function polyAt(P, f) {
  if (P.length === 2) return seg(P[0], P[1], f);
  let d = f * polyLen(P);
  for (let i = 1; i < P.length - 1; i++) {
    const l = dist2(P[i - 1], P[i]);
    if (d < l) return seg(P[i - 1], P[i], d / l);
    d -= l;
  }
  const n = P.length - 1;
  return seg(P[n - 1], P[n], d / dist2(P[n - 1], P[n]));
}
/** where slot S of crossing X is at time t in the minute starting walk0 (null: not here); returns [x, y, walking, dx, dy] */
function xingPos(X, S, k, dt) {
  const hk = (n) => hash(X.id * 31 + S.i, k, n);
  if (hk(1) > 0.82) return null;                                                     // some minutes this slot stays empty
  const o = S.off + (hk(2) - 0.5) * 0.4, sh = (p) => [p[0] + X.sp[0] * o, p[1] + X.sp[1] * o];
  const [E, A, B, Xp] = [X.path[0], sh(X.path[1]), sh(X.path[2]), X.path[3]], [vIn, vOut] = X.via || [];
  const pIn = vIn ? [E, vIn, A] : [E, A], pOut = vOut ? [B, vOut, Xp] : [B, Xp];
  const v = 1.15 + hk(3) * 0.4, tArr = -6 - hk(4) * 22, dEA = polyLen(pIn) / v, tS = 0.3 + S.i * 0.7 + hk(5) * 2.5;
  const tB = tS + dist2(A, B) / v, tX = tB + polyLen(pOut) / v;
  if (dt < tArr - dEA || dt >= tX) return null;
  if (dt < tArr) return polyAt(pIn, (dt - tArr + dEA) / dEA);
  if (dt < tS) return [A[0], A[1], false, B[0] - A[0], B[1] - A[1]];
  if (dt < tB) return seg(A, B, (dt - tS) / (tB - tS));
  return polyAt(pOut, (dt - tB) / (tX - tB));
}
/** the screen direction someone walking along (dx, dy) seems to go: view and dir for person() */
function facing(wx, wy, dx, dy) {
  const a = proj(wx, wy, 0), b = proj(wx + dx * 0.1, wy + dy * 0.1, 0), mx = b[0] - a[0], my = b[1] - a[1];
  if (Math.abs(my) > Math.abs(mx) * 0.9) return { view: my > 0 ? 1 : 0, dir: 1 };
  return { view: 2, dir: mx > 0 ? 1 : -1 };
}
function addCrossers(DL, t, crowd) {
  for (const X of XINGS) for (const S of X.slots) {
    if (!(S.rank < crowd || (S.rank === 0 && crowd > 0))) continue;
    for (let k = 0; k < 4; k++) {
      let dt = t - (k * SIG_T + X.walk);
      dt = ((dt % LOOP) + LOOP) % LOOP; if (dt >= LOOP / 2) dt -= LOOP;
      const p = xingPos(X, S, k, dt);
      if (!p) continue;
      const [x, y, walking, dx, dy] = p, li = X.id * 40 + S.i * 4 + k, f = facing(x, y, dx, dy);
      const nearDoor = Math.hypot(x - DOORS[0], y - DOORS[1]);
      const z = nearDoor < 6 ? 1.5 * (1 - nearDoor / 6) : 0;                       // up the steps under the clocks
      if (nearDoor < 1.2) continue;                                                  // gone in through the doors (or just come out)
      const u = depthOf(x, y), umb = umbOf(li, t);
      DL.push([u, () => personAt(x, y, z, li, { view: f.view, dir: f.dir, step: walking && STRIDE(t + S.i * 0.15), umb, aR: umb ? 2 : 0, phone: !walking && !umb && hash(li, 5, 9) < 0.4 })]);
    }
  }
}
/* ================= People who stay ================= */
// waiting under the clocks, on the steps (far: a few pixels each); on the platforms; the busker, the barista, the
// coffee cart's customers (one every 40 s)
const CLOCK_CROWD = [[-19.6, -21.4], [-21.6, -19.4], [-22.6, -21.8], [-20.4, -23.2], [-23.6, -20.2], [-18.6, -23.6], [-24.4, -22.6]];
const BUSKER = { x: 12.9, y: 12.9 }, CART = { x: 14.6, y: 19.8 }, BIN = { x: 13.6, y: 16.6 };
const PLATFORM_PEOPLE = [{ y: 27, li: 300, rank: 0.25 }, { y: 31, li: 301, rank: 0.6 }, { y: 23.5, li: 302, rank: 0.85 }];
const CC_PEOPLE = [{ x: -26, li: 310, rank: 0, alight: false }, { x: -30, li: 311, rank: 0.4, alight: false }, { x: -22, li: 312, rank: 0.7, alight: true }];
const COUPLE = { x: 18.2, y: 18.6 };                  // on St Paul's forecourt under one umbrella, watching the crossing
function addStayers(DL, t, crowd) {
  const here = r => crowd > 0 && (r === 0 || r < crowd);
  if (here(0.3)) {
    const lean = frac(t / 30 + 0.3) < 0.2, point = frac(t / 60 + 0.55) < 0.1;   // she leans in every 30 s; he points at the tram now and then
    DL.push([depthOf(COUPLE.x, COUPLE.y), () => {
      personAt(COUPLE.x + 0.5, COUPLE.y + 0.3, 0, 41, { view: 0, aR: point ? 3 : 0 });
      personAt(COUPLE.x - 0.1, COUPLE.y - 0.2, 0, 43, { view: 0, umb: 1, aL: 2, lean: lean ? 1 : 0 });
    }]);
  }
  CLOCK_CROWD.forEach(([x, y], i) => {
    if (!here(rankOf(i, 0.41))) return;
    const leave = frac(t / 80 + i * 0.37) > 0.86;                                    // now and then one goes in, another comes
    if (leave) return;
    DL.push([depthOf(x, y), () => personAt(x, y, 1.5, 200 + i, { view: i % 3 === 0 ? 1 : 0, umb: i % 2 ? 1 : 0 }, false)]);
  });
  // the Swanston Street platform: they wait, the tram stops in front of them, they're gone when it leaves
  for (const P of PLATFORM_PEOPLE) {
    if (!here(P.rank)) continue;
    const gone = [[48, 130], [168, 250]].some(([a, b]) => (t >= a && t < b) || t + 240 < b && t + 240 >= a);
    const arrive = [[10, 48], [130, 168]].find(([a, b]) => t >= a && t < b);
    if (gone || !arrive) continue;
    const f = clamp((t - arrive[0] - (P.li - 300) * 4) / 8, 0, 1);
    if (f <= 0) continue;
    const y = lerp(14, P.y, f), x = -4.4;
    DL.push([depthOf(x, y), () => personAt(x, y, 0.3, P.li, { view: f < 1 ? 1 : 2, dir: -1, step: f < 1 && STRIDE(t), umb: umbOf(P.li, t) || (P.li === 300 ? 1 : 0), aR: 2, phone: f >= 1 && P.li !== 300 })]);
  }
  // the City Circle's platform: two wait for it and board; one gets off and walks to the station
  for (const P of CC_PEOPLE) {
    if (!here(P.rank)) continue;
    let x = P.x, y = -4.3, walking = false, dirx = 1;
    if (!P.alight) { if (t < 60 || t >= CC.stop0 + 3 + (P.li - 310) * 2) continue; }
    else {
      if (t < CC.stop0 + 4 || t >= CC.stop0 + 40) continue;
      const f = (t - CC.stop0 - 4) / 36; walking = true;
      if (f < 0.2) { x = P.x; y = lerp(-3.4, -5, f / 0.2); } else { x = lerp(P.x, -22.5, (f - 0.2) / 0.8); y = lerp(-5, -18.5, (f - 0.2) / 0.8); }
      dirx = -0.2;
      if (f > 0.93) continue;                                                       // in through the doors
    }
    const f2 = facing(x, y, dirx, 0);
    DL.push([depthOf(x, y), () => personAt(x, y, 0.3, P.li, { view: walking ? f2.view : 1, dir: f2.dir, step: walking && STRIDE(t), umb: P.li === 311 ? 1 : umbOf(P.li, t), aR: 2 })]);
  }
}

/* ================= The busker, the coffee cart, the bin, the lamps ================= */
function drawBusker(t, crowd) {
  const [sx, , u] = proj(BUSKER.x, BUSKER.y, 0), fy = Math.round(rowOf(0, u)), h = Math.round(FL * 1.8 / u), P = look(h, 7);
  const x = Math.round(sx);
  // the open guitar case in front of him, coins in its red lining
  const cy = fy + 2;
  R(x - 9, cy - 1, 12, 4, '#141016'); R(x - 8, cy, 10, 2, '#6a1c24');
  for (const [i, j] of [[-6, 0], [-3, 1], [-1, 0], [-5, 1]]) dot(x + i, cy + j, '#e8c060');
  if (COINS.some(c => t >= c && t < c + 0.6)) halo(HL.coin, x - 3, cy, 0.7);
  if (crowd === 0) return;                                                          // the case stays; he goes, with his guitar
  ctx.save(); ctx.globalAlpha = 0.18; ctx.setTransform(1, 0, 0, -1, 0, 2 * fy + 2); person(x, fy, P, { view: 1 }); ctx.restore();
  const strum = step(t, 1440) % 3 !== 0;                                            // his right hand on the strings
  const hy = person(x, fy, P, { view: 1, aL: 3, aR: strum ? 3 : 0 });
  // the guitar across him: the body low on his right, the neck up to his left hand
  const by = hy + P.hh + Math.round(P.th * 0.6), gx = x + 1;
  R(gx - 3, by - 3, 6, 6, '#b8783e'); R(gx - 2, by - 4, 4, 1, '#c8884a'); R(gx - 2, by + 3, 4, 1, '#8a5428');   // its body, lit warm
  R(gx - 3, by - 3, 6, 1, '#e0a462'); R(gx, by - 1, 2, 2, '#2a1810');                                          // the shine, the sound hole
  for (let k = 1; k <= 9; k++) dot(gx - 3 - k, by - 3 - Math.round(k * 0.5), k < 9 ? '#5a3a20' : '#2a1810');   // the neck to his left hand
  R(x - 2, hy - 1, 4, 2, '#2a4a6a');                                                 // a beanie
  // a passer-by drops a coin in the case, then walks on
  for (const c of COINS) {
    if (t < c - 4 || t >= c + 5) continue;
    const k = along([[c - 4, 0], [c - 0.6, 1], [c + 0.6, 1], [c + 5, 2]], t, false).v, stopped = k === 1, li = 500 + c;
    const wx = k < 1 ? lerp(19, 13.9, k) : lerp(13.9, 19.5, k - 1), wy = k < 1 ? lerp(16.5, 13.7, k) : lerp(13.7, 11.2, k - 1);
    const f = facing(wx, wy, k < 1 ? -5 : 5, k < 1 ? -2.8 : -2.5);
    personAt(wx, wy, 0, li, { view: stopped ? 2 : f.view, dir: stopped ? -1 : f.dir, step: !stopped && STRIDE(t), umb: umbOf(li, t), aR: stopped ? 3 : umbOf(li, t) ? 2 : 0 });
  }
}
let CART_C = null, BIN_C = null, LAMP_C = [];
/** prerender a box (the cart, the bin) with drawBox onto its own canvas */
function prebox(parts) {
  const [c, x] = makeCanvas(W, H), keep = ctx;
  ctx = x;
  for (const [cx, cy, hd, L, Bw, Hz, side, roof] of parts) drawBox(cx, cy, hd, L, Bw, Hz, side, roof);
  ctx = keep;
  const p = pix(W, H); p.x.drawImage(c, 0, 0); p.d.set(p.x.getImageData(0, 0, W, H).data);
  return trim(p);
}
function renderCart() {
  const panel = (name, s, sx, u, len, mir) => {
    zfill(sx, u, 0.05, 0.25, '#0e0e14', mir);
    zfill(sx, u, 0.25, 0.95, name === 'right' || name === 'left' ? (frac(s / 0.45) < 0.15 ? '#1a3a26' : '#245a38') : '#1e4a2e', mir);
    zfill(sx, u, 0.95, 1.05, '#c8b896', mir);
  };
  const machine = (name, s, sx, u, len, mir) => { if (!mir) { zfill(sx, u, 1.05, 1.42, s < 0.08 ? '#8a8e9a' : '#c8ccd6'); zfill(sx, u, 1.2, 1.26, '#e8a040'); } };
  const pole = (name, s, sx, u) => zfill(sx, u, 1.05, 2.2, '#2a2a30');
  const awn = (name, s, sx, u) => zfill(sx, u, 2.2, 2.32, frac(s / 0.4) < 0.5 ? '#e8e0cc' : '#2a7a4a');
  return prebox([
    [CART.x, CART.y, Math.PI / 2, 1.9, 1.0, 1.05, panel, '#d8c8a4'],
    [CART.x - 0.1, CART.y + 0.3, Math.PI / 2, 0.5, 0.5, 1.42, machine, '#d8dce4'],
    [CART.x + 0.4, CART.y - 0.85, 0, 0.06, 0.06, 2.2, pole, null], [CART.x + 0.4, CART.y + 0.85, 0, 0.06, 0.06, 2.2, pole, null],
    [CART.x - 0.1, CART.y, Math.PI / 2, 2.3, 1.7, 2.32, awn, '#e8e0cc'],
  ]);
}
function renderBin() {
  return prebox([[BIN.x, BIN.y, 0, 0.55, 0.55, 1.0, (name, s, sx, u, len, mir) => {
    zfill(sx, u, 0.02, 0.9, s < 0.05 ? '#2a3a30' : '#18241e', mir); zfill(sx, u, 0.9, 1.0, '#3a4a40', mir);
  }, '#202c26']]);
}
/** a lamp post: a dark green column, a lantern on top (its light is drawn with the halos) */
function renderLamp(L) {
  const p = pix(W, H), P = postAt(L.x, L.y, L.z - 0.6), w = P.u < 14 ? 2 : 1;
  for (let y = P.y1; y <= P.y0; y++) { p.set(P.x, y, '#16241e'); if (w > 1) p.set(P.x + 1, y, '#0c1410'); }
  for (let y = P.y0 - 3; y <= P.y0; y++) for (let x = P.x - 1; x <= P.x + w; x++) p.set(x, y, '#16241e');   // its foot
  const [lx, ly0] = proj(L.x, L.y, L.z - 0.6), [, ly1] = proj(L.x, L.y, L.z + 0.3), lw = Math.max(2, Math.round(0.5 * FL / P.u));
  const top = Math.round(ly1), bot = Math.round(ly0), cxl = Math.round(lx);
  for (let y = top; y <= bot; y++) {                                                 // the lantern: a glass taper under a cap and a finial
    const f = (y - top) / Math.max(1, bot - top), hw = Math.max(1, Math.round(lw * (0.95 - 0.45 * f)));
    for (let x = -hw; x <= hw; x++) p.set(cxl + x, y, Math.abs(x) === hw ? '#16241e' : f < 0.4 ? '#fff6dc' : '#ffd890');
  }
  for (let x = -lw - 1; x <= lw + 1; x++) p.set(cxl + x, top - 1, '#16241e');
  for (let k = 2; k <= Math.max(2, Math.round(lw * 0.8)) + 1; k++) for (let x = -Math.max(0, lw - k); x <= Math.max(0, lw - k); x++) p.set(cxl + x, top - k, '#1e3028');
  p.set(cxl, top - Math.round(lw * 0.8) - 3, '#1e3028');
  return { ...trim(p), lx: Math.round(lx), ly: Math.round((ly0 + ly1) / 2), u: P.u };
}
/* the barista behind the counter, facing us; the customer of every 40 s comes up the forecourt to the cart's near end
   (never in front of its counter or behind it), waits, takes the cup and walks back. The cart's body stands on x
   CART.x ± 0.5, y CART.y ± 0.95: the barista is on its far side and drawn before it, the customer off its north end */
const BARISTA = { x: CART.x - 0.85, y: CART.y - 0.3 }, CUSTOMER = { x: CART.x + 1.1, y: CART.y + 1.25, from: [CART.x + 3.4, 32.5] };
function addCart(DL, t, crowd) {
  const here = r => crowd > 0 && (r === 0 || r < crowd);
  const cu = depthOf(CART.x, CART.y);
  DL.push([cu, () => blit(CART_C)]);
  const c = Math.floor(t / COFFEE_T), tc = t - c * COFFEE_T, steam = tc > 14 && tc < 22;
  if (here(0)) {
    const { x: bx, y: by } = BARISTA;
    DL.push([depthOf(bx, by), () => personAt(bx, by, 0, 9, { view: 1, aR: steam || (tc > 22 && tc < 25) ? 3 : 0, aL: steam ? 3 : 0 }, false)]);
    if (steam) {                                                                     // the steam wand, frothing the milk
      const [mx, my] = proj(CART.x - 0.1, CART.y + 0.3, 1.5);
      DL.push([cu - 0.02, () => { for (let k = 0; k < 4; k++) { const { u } = cycle(t, 480, k / 4); ctx.globalAlpha = 0.45 * (1 - u); dot(mx + wave(t, 60, k * 0.25) * 1.5, my - u * 8, '#dfe4f0'); } ctx.globalAlpha = 1; }]);
    }
  }
  const li = 400 + c;
  if (!here(rankOf(c, 0.7))) return;
  const { x: qx, y: qy, from: [ox, oy] } = CUSTOMER;
  let wx, wy, walking = true, dx = qx - ox, dy = qy - oy;
  if (tc < 9) { wx = lerp(ox, qx, tc / 9); wy = lerp(oy, qy, tc / 9); }
  else if (tc < 26) { wx = qx; wy = qy; walking = false; }
  else if (tc < 38) { wx = lerp(qx, ox, (tc - 26) / 12); wy = lerp(qy, oy, (tc - 26) / 12); dx = -dx; dy = -dy; }
  else return;
  const f = walking ? facing(wx, wy, dx, dy) : { view: 2, dir: -1 }, umb = umbOf(li, t);   // waiting, they face the counter
  DL.push([depthOf(wx, wy), () => personAt(wx, wy, 0, li, { view: f.view, dir: f.dir, step: walking && STRIDE(t), umb, aR: tc >= 25 || umb ? 2 : 0 })]);
}

/* ================= Gulls: one after a chip by the bin, a few on the station's parapet, two wheeling over the dome ================= */
const GULL_KEY = { w: '#eef0f4', g: '#9aa4bc', k: '#2a3050', r: '#e04a3a' };
const GULL_F = [
  ['k.......k', '.gw...wg.', '..wwwww..', '....wr...'],
  ['.........', 'kgww.wwgk', '..wwwww..', '....wr...'],
  ['.........', '..wwwww..', '.gwwwrwg.', 'k.......k'],
];
const GULL_STAND = [['..ww.', '.wwwr', 'gwww.', '.gg..', '..r..'], ['.....', '..ww.', 'gwwwr', '.gg..', '..r..']];
const CHIP_AT = { x: 12.9, y: 16.0 };
let GULL_SPR, GULL_SMALL, GULL_STAND_C;
function drawChipGull(DL, t) {
  const W0 = CHIP.find(([a, b]) => t >= a && t < b);
  if (!W0) return;
  const [a, b] = W0, [gx, gy, u] = proj(CHIP_AT.x, CHIP_AT.y, 0);
  DL.push([u, () => {
    if (t < a + 3) {                                                                 // gliding in, down to the chip
      const f = (t - a) / 3;
      ctx.drawImage(GULL_SPR[step(t, 960) % 3][1], Math.round(lerp(gx + 90, gx - 4, f)), Math.round(lerp(110, gy - 6, f * f)));
    } else if (t < b - 3) {
      const peck = hash(step(t, 480), a, 17) < 0.35, hop = Math.round(Math.sin((t - a) * 0.5) * 3);
      const x = Math.round(gx + hop), y = Math.round(gy) - 4;
      ctx.globalAlpha = 0.25; ctx.drawImage(GULL_STAND_C[peck ? 1 : 0], x - 2, y + 10, 5, -5); ctx.globalAlpha = 1;
      ctx.drawImage(GULL_STAND_C[peck ? 1 : 0], x - 2, y);
      dot(Math.round(gx) + 2, Math.round(gy), '#e8c060');                             // the chip
    } else {                                                                         // off to the dome
      const f = (t - b + 3) / 3;
      ctx.drawImage(GULL_SPR[step(t, 1440) % 3][1], Math.round(lerp(gx - 2, 210, f)), Math.round(lerp(gy - 6, 80, Math.sqrt(f))));
    }
  }]);
}
const PARAPET_GULLS = [[-24, -15.3, 22.2], [-21, -18.3, 22.2], [-40, -15.2, 19.6], [-58, -15.2, 19.6]];
const DOME_GULLS = [{ rx: 26, ry: 9, cy: 62, k: 6, ph: 0 }, { rx: 34, ry: 12, cy: 48, k: -5, ph: 0.45 }];
function drawGulls(t) {
  const [dx] = proj(DOME.x, DOME.y, 30);
  DOME_GULLS.forEach((g, i) => {                                                    // wheeling over the dome, lit from below
    const a = TAU * (g.k * t / LOOP + g.ph), x = dx + g.rx * Math.sin(a), y = g.cy + g.ry * Math.cos(a) * 0.5 + 2 * Math.sin(a * 2);
    ctx.drawImage(GULL_SMALL[[0, 1, 2, 1][(step(t, 720) + i * 2) & 3]][g.k * Math.cos(a) > 0 ? 0 : 1], Math.round(x) - 3, Math.round(y) - 1);
  });
  PARAPET_GULLS.forEach(([x, y, z], i) => {                                         // on the parapet; up at the hour, back by 232
    const [px, py] = proj(x, y, z), { t0, t1 } = GULLS_UP, off = t0 + i * 0.4, back = t1 - 6 + i * 1.2;
    if (t < off || t >= back) { dot(px, py - 1, '#e8ecf4'); dot(px + 1, py - 1, '#c8cede'); dot(px, py, '#8a90a6'); return; }
    const f = (t - off) / (back - off), a = TAU * f * 1.5 + i;
    const x2 = f < 0.12 ? lerp(px, px + (i - 1.5) * 12, f / 0.12) : f > 0.88 ? lerp(px + (i - 1.5) * 12, px, (f - 0.88) / 0.12) : px + (i - 1.5) * 12 + 30 * Math.sin(a);
    const y2 = f < 0.12 ? lerp(py, py - 30, f / 0.12) : f > 0.88 ? lerp(py - 30, py - 1, (f - 0.88) / 0.12) : py - 30 - 10 * Math.cos(a);
    ctx.drawImage(GULL_SMALL[[0, 1, 2, 1][(step(t, 960) + i) & 3]][Math.cos(a) > 0 ? 0 : 1], Math.round(x2) - 3, Math.round(y2) - 1);
  });
}
/* ================= The cyclist: down Swanston Street, and back up later ================= */
const RIDES = [{ t0: 70, t1: 90, x: 8.2, y0: 42, y1: -80 }, { t0: 190, t1: 206, x: -8.2, y0: -80, y1: 42 }];
function addCyclist(DL, t, crowd) {
  if (!(crowd > 0.35)) return;                                                      // rank 0.35: the bike goes with its rider
  for (const Rd of RIDES) {
    if (t < Rd.t0 || t >= Rd.t1) continue;
    const y = lerp(Rd.y0, Rd.y1, (t - Rd.t0) / (Rd.t1 - Rd.t0)), x = Rd.x, u = depthOf(x, y);
    DL.push([u, () => {
      const [sx, , uu] = proj(x, y, 0), k = FL / uu, fy = Math.round(rowOf(0, uu)), r = Math.max(1, Math.round(0.34 * k));
      if (zat(sx, fy - 2) < uu || sx < -10 || sx > W + 10) return;
      const f = facing(x, y, 0, Rd.y1 - Rd.y0), d = f.view === 2 ? f.dir : 1, gap = Math.max(2, Math.round(1.1 * k * (f.view === 2 ? 1 : 0.3)));
      for (const wx of [Math.round(sx - gap / 2), Math.round(sx + gap / 2)]) {     // two wheels
        for (let a = 0; a < 12; a++) dot(wx + Math.round(Math.cos(a / 12 * TAU) * r), fy - r + Math.round(Math.sin(a / 12 * TAU) * r), '#2a2a34');
      }
      const P = look(Math.max(12, Math.round(FL * 1.5 / uu)), 77);
      if (P.h >= 12) person(Math.round(sx), fy - r, P, { view: f.view, dir: d, aR: 3 });
      halo(HL.bike, Math.round(sx + d * gap / 2 + d), fy - r - 1, 0.8);
      dot(Math.round(sx + d * gap / 2 + d), fy - r - 1, '#fff6dc'); dot(Math.round(sx - d * gap / 2 - d), fy - r - 1, '#ff3a2a');
    }]);
  }
}

/* ================= The drizzle, the city's own: three depths, heavier in the shower, lit where it passes a light ================= */
const showerAt = t => smooth(SHOWER.a, SHOWER.b, t) * (1 - smooth(SHOWER.c, SHOWER.d, t));
const DRZ = [
  { count: 190, extra: 210, nMin: 70, nMax: 92, len: 1, slope: 0.1, col: '#8a88b2', a: 0.32, land: [96, HZ + 4], lit: false },
  { count: 110, extra: 140, nMin: 100, nMax: 124, len: 2, slope: 0.14, col: '#a4a2cc', a: 0.42, land: [HZ + 6, 238], lit: true },
  { count: 46, extra: 70, nMin: 132, nMax: 156, len: 4, slope: 0.18, col: '#c4c4e6', a: 0.5, land: [H + 6, H + 6], lit: true },
];
DRZ.forEach((L, li) => {
  const r = rng(700 + li);
  L.drops = [];
  for (let i = 0; i < L.count + L.extra; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() });
});
// light volumes the drops catch: [x, y, rx, ry, colour]
const DRZ_LIGHTS = [[208, 140, 46, 40, '#ffd498'], [290, 150, 44, 22, '#ffc888'], [150, 150, 26, 26, '#ffcc90'], [420, 184, 40, 20, '#ffcf8c'],
  [410, 124, 26, 36, '#fff0c8'], [68, 98, 30, 50, '#fff0c8'], [40, 200, 40, 16, '#ffd8a0']];
const DRZ_LIGHTS_BB = [...DRZ_LIGHTS, [BB.x + BB.w / 2, BB.y + 14, 34, 18, '#f0e4c0']];
function drawDrizzle(L, t, lights) {
  const sh = showerAt(t), n = L.count + Math.round(L.extra * sh), len = L.len + (sh > 0.5 ? 1 : 0), slope = L.slope + sh * 0.06;
  for (let i = 0; i < n; i++) {
    const d = L.drops[i], { cyc, u } = cycle(t, d.n, d.ph);
    const yl = lerp(L.land[0], L.land[1], hash(d.id, cyc, 12)), fall = yl + 10;
    const xl = -20 + hash(d.id, cyc, 11) * (W + 30 + fall * slope);
    const yh = yl - fall * (1 - u), xh = xl - (yl - yh) * slope;
    if (yh < -2 || yh > H) continue;
    let col = L.col, a = L.a;
    if (L.lit) for (const [lx, ly, rx, ry, lc] of lights) { const dx = (xh - lx) / rx, dy = (yh - ly) / ry; if (dx * dx + dy * dy < 1) { col = lc; a = L.a + 0.25; break; } }
    ctx.globalAlpha = a; ctx.fillStyle = col;
    for (let k = 0; k < len; k++) ctx.fillRect(Math.round(xh - k * slope), Math.round(yh - k), 1, 1);
  }
  ctx.globalAlpha = 1;
}
const RINGS = [];
{
  const r = rng(73);
  for (let i = 0; i < 70; i++) RINGS.push({ id: i, n: 90 + Math.floor(r() * 50), ph: r() });
}
function drawRings(t) {
  const sh = showerAt(t), n = 30 + Math.round(40 * sh);
  ctx.fillStyle = '#9a9ccc';
  for (let i = 0; i < n; i++) {
    const g = RINGS[i], { cyc, u } = cycle(t, g.n, g.ph);
    if (u > 0.4) continue;
    const s = u / 0.4, sy = Math.round(HZ + 12 + hash(g.id, cyc, 2) * 92), sx = Math.round(hash(g.id, cyc, 1) * W);
    if (zat(sx, sy) < 900) continue;                                                 // not on a building's face
    const [, , uu] = groundAt(sx, sy), rr = (0.6 + s * 2.4) * Math.min(1.6, 14 / uu) + 0.6, ry = rr * Math.min(0.5, (sy - HZ) / 60), pts = 6 + Math.floor(rr * 2);
    ctx.globalAlpha = (1 - s) * 0.5;
    for (let j = 0; j < pts; j++) { const a = j / pts * TAU; ctx.fillRect(Math.round(sx + Math.cos(a) * rr), Math.round(sy + Math.sin(a) * ry), 1, 1); }
  }
  ctx.globalAlpha = 1;
}
// a picked weather (site/js/engine/weather.js): Melbourne draws its own drizzle; the wet road stays in every pick.
// Far drops end on Southbank, mid ones on the crossing, near ones fall past the bottom; no tree in view, so leaves
// ride the wind across the open air over the crossing
const WEATHER = weatherPlans(['drizzle']);
const WX_LAND = [[100, HZ + 4], [HZ + 6, 236], [H + 4, H + 4]];
const wxLights = list => list.map(([x, y, rx, ry, col]) => ({ x, y, rx, ry, col }));
const WX = { land: WX_LAND, lights: wxLights(DRZ_LIGHTS), leaves: { wind: [0, 30, W, 200] } };
const WX_BB = { ...WX, lights: wxLights(DRZ_LIGHTS_BB) };

/* ================= Prerendered state ================= */
let HL, SKY_C, CLOUDS, FAR_C, CITY_C, ST_C, LIT_C, FED_C, YJ_C, GROUND_C, MARKS_C, WIRES_C, SHELTER_C, BOARD_C, ADS, PITCH, VIG_C, POSTS;
const SPLIT = {};
const TCLOCK_AT = { x: 0, y: 0 };
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.76, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.5) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [6, 6, 18], Math.round(q * 0.42 * 255));
  }
  return p.done();
}
function prerender() {
  for (const L of [WINS, SWIN, CLOCKS, BULBS, SB_LIGHTS, LAMP_C]) L.length = 0;
  winIds.clear(); ZBUF.fill(Infinity);
  HL = {
    lamp: makeHalo(44, '#ffc878', 0.2, 1.7), lampCore: makeHalo(10, '#fff0c8', 0.5, 1.3), dome: makeHalo(40, '#ffb468', 0.14, 1.6),
    domeTop: makeHalo(12, '#9cead0', 0.18, 1.4), lantern: makeHalo(8, '#ffe2a0', 0.4, 1.3), flood: makeHalo(26, '#ffa860', 0.14, 1.5),
    arch: makeHalo(22, '#ffd290', 0.18, 1.5), clock: makeHalo(3, '#fff4e0', 0.4, 1.1), tclock: makeHalo(6, '#fff0d0', 0.36, 1.2),
    bulb: makeHalo(2, '#ffe2a0', 0.4, 1), crown: makeHalo(14, '#ffd070', 0.22, 1.4), red: makeHalo(3, '#ff4038', 0.5, 1),
    sig: { r: makeHalo(4, '#ff4030', 0.5, 1.2), a: makeHalo(4, '#ffb030', 0.5, 1.2), g: makeHalo(4, '#40f0a8', 0.45, 1.2) },
    head: makeHalo(8, '#fff2d0', 0.45, 1.3), tail: makeHalo(4, '#ff3a2a', 0.4, 1.2), amber: makeHalo(4, '#ffa020', 0.5, 1.2),
    bar: makeHalo(30, '#ffb060', 0.12, 1.6), bb: makeHalo(30, '#f0e2c0', 0.12, 1.6), coin: makeHalo(3, '#ffe890', 0.6, 1),
    bike: makeHalo(5, '#fff2d0', 0.4, 1.2), plane: makeHalo(3, '#ffffff', 0.4, 1),
  };
  SKY_C = renderSky(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  const r = rng(1835);                                                               // the year Melbourne was founded
  SPLIT.far = WINS.length; { const p = pix(W, H); paintSouthbank(p, r); FAR_C = trim(p); }
  SPLIT.city = WINS.length; { const p = pix(W, H); paintCity(p, r); CITY_C = trim(p); }
  SPLIT.end = WINS.length;
  LITP = pix(W, H);
  { const p = pix(W, H); paintBeyond(p, r); paintTower(p); paintStation(p, r); ST_C = trim(p); }
  { const p = pix(W, H); FED_DEPTH = paintWall(p, FED.x0, FED.y, 1, 0, FED.x1 - FED.x0, 0, 19, fedMat); FED_C = trim(p); }
  { const p = pix(W, H); YJ_DEPTH = paintWall(p, YJ.x, YJ.y0, 0, 1, YJ.y1 - YJ.y0, 0, YJ.h, yjMat); YJ_C = trim(p); }
  LIT_C = LITP.done();
  { const [x, y] = proj(TOWER.x1, TOWER.y1 + 0.5 - TCLOCK.s, TCLOCK.z); TCLOCK_AT.x = Math.round(x); TCLOCK_AT.y = Math.round(y); }
  layBulbs();
  GROUND_C = renderGround(); MARKS_C = renderMarks(); WIRES_C = renderWires(); SHELTER_C = renderShelters();
  BOARD_C = renderBoard(); ADS = [0, 1, 2].map(renderAd); PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  CART_C = renderCart(); BIN_C = renderBin(); for (const L of LAMPS) LAMP_C.push(renderLamp(L));
  POSTS = POLES.map(([x, y]) => postAt(x, y, 6.8));
  GULL_SPR = GULL_F.map(rows => [sprite(rows, GULL_KEY), sprite(rows, GULL_KEY, true)]);
  const small = [['k...k', '.www.', '..r..'], ['.....', 'kwwwk', '..r..'], ['.....', '.www.', 'k...k']];
  GULL_SMALL = small.map(rows => [sprite(rows, { ...GULL_KEY, w: '#f2e6d4' }), sprite(rows, { ...GULL_KEY, w: '#f2e6d4' }, true)]);
  GULL_STAND_C = GULL_STAND.map(rows => sprite(rows, GULL_KEY));
  VIG_C = renderVignette();
}

/* ================= Per-frame ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const L of CLOUDS) { const off = scroll(t, L.k, W); ctx.drawImage(L.c, -off, L.y); ctx.drawImage(L.c, W - off, L.y); }
  const sh = showerAt(t);
  if (sh > 0.01) { ctx.globalAlpha = 0.28 * sh; ctx.fillStyle = '#0a0a18'; ctx.fillRect(0, 0, W, HZ + 4); ctx.globalAlpha = 1; }   // the shower lowers the sky
  if (t >= 46 && t < 84) {                                                           // an aeroplane, far off, heading for Tullamarine
    const f = (t - 46) / 38, x = lerp(-10, W + 10, f), y = lerp(30, 18, f);
    dot(x, y, '#c8cce0'); if (step(t, 240) % 2) dot(x - 1, y, '#ff4a40'); else dot(x + 1, y, '#40f080');
    if (frac(t) < 0.12) halo(HL.plane, x, y, 0.6);
  }
}
function drawFar(t) {
  blit(FAR_C);
  drawWins(SPLIT.far, SPLIT.city, t, 0.85);
  halo(HL.crown, (EUR.x0 + EUR.x1) / 2, EUR.top + 6, 0.8 + 0.12 * wave(t, 6));
  for (const [x, y, ph] of SB_LIGHTS) { const v = 0.5 + 0.5 * wave(t, 120, ph); if (v > 0.3) { ctx.globalAlpha = v; dot(x, y, '#ff3a30'); ctx.globalAlpha = 1; halo(HL.red, x, y, v * 0.6); } }
}
const bellLevel = t => { let b = 0; for (const tb of BELLS) { const d = t - tb; if (d >= 0 && d < 3) b = Math.max(b, Math.exp(-d * 1.1) * (1 - d / 3)); } return b; };
function drawStation(t, clock) {
  const [dx, dyTop] = proj(DOME.x, DOME.y, DOME.z1), [, dyBase] = proj(DOME.x, DOME.y, DOME.z0), b = bellLevel(t);
  halo(HL.dome, dx, dyBase + 6, 0.9 + 0.05 * wave(t, 4));
  blit(ST_C);
  for (const w of SWIN) if (w.x0 < 330 && winLit(w, t)) ctx.drawImage(LIT_C, w.x0, w.y0, w.x1 - w.x0 + 1, w.y1 - w.y0 + 1, w.x0, w.y0, w.x1 - w.x0 + 1, w.y1 - w.y0 + 1);
  // the floodlights along the foot of the façade, the warm arch, the dome's crown and lantern
  for (const x of [-34, -50, -68, -86, -104]) { const [fx, fy] = proj(x, -14, 1); halo(HL.flood, fx, fy, 0.8); }
  { const [ax, ay] = proj(-21, -21, 8); halo(HL.arch, ax, ay, 0.85); }
  halo(HL.domeTop, dx, dyTop + 6, 0.7 + 0.1 * wave(t, 5, 0.3));
  halo(HL.lantern, dx, dyTop - 3, 0.75 + 0.15 * wave(t, 10) + b * 0.8);
  // the bulbs, each shimmering slowly on its own (no chase, no flashing); brighter while the bells ring
  ctx.fillStyle = '#ffe6a8';
  for (const [x, y, k, ph] of BULBS) { ctx.globalAlpha = Math.min(1, 0.62 + 0.3 * wave(t, k, ph) + b * 0.3); ctx.fillRect(x, y, 1, 1); }
  ctx.globalAlpha = 1;
  for (let i = 0; i < BULBS.length; i += 9) halo(HL.bulb, BULBS[i][0], BULBS[i][1], 0.7);
  // the row of clocks: white faces, each showing the next train on its line
  CLOCKS.forEach(([x, y], i) => { R(x, y, 2, 2, '#f4eedc'); if (i % 3 === 1) dot(x + 1, y + 1, '#cfc6b0'); });
  if (CLOCKS.length) halo(HL.arch, CLOCKS[4][0], CLOCKS[4][1], 0.3);
  // the tower's clock shows the city's time (10:10 without it); its face brightens on the hour
  halo(HL.tclock, TCLOCK_AT.x, TCLOCK_AT.y, 0.7 + b);
  hands(ctx, TCLOCK_AT.x, TCLOCK_AT.y, clock, 610, 1, 2, '#2a1e18');
}
function drawCorners(t, billboard, pitch) {
  blit(CITY_C);
  drawWins(SPLIT.city, SPLIT.end, t, 0.9);
  blit(FED_C);
  blit(YJ_C);
  for (const w of SWIN) if (w.x0 >= 330 && winLit(w, t)) ctx.drawImage(LIT_C, w.x0, w.y0, w.x1 - w.x0 + 1, w.y1 - w.y0 + 1, w.x0, w.y0, w.x1 - w.x0 + 1, w.y1 - w.y0 + 1);
  { const [bx, by] = proj(-15, 26, 2.2); halo(HL.bar, bx, by, 0.85 + 0.08 * wave(t, 12)); }
  { const [fx, fy] = proj(18, -15, 2); halo(HL.bar, fx, fy, 0.7); }
  if (billboard) drawBoard(t, pitch);
}
const BB_FLICK = [[64.2, 65.4], [188.6, 189.2]];       // the sign's lamps stutter now and then
function drawBoard(t, pitch) {
  const fl = BB_FLICK.some(([a, b]) => t >= a && t < b) && hash(step(t, 2400), 13) < 0.5;
  halo(HL.bb, BB.x + 12, BB.y + 6, fl ? 0.3 : 0.85); halo(HL.bb, BB.x + 41, BB.y + 6, 0.85);
  blit(BOARD_C);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(ADS[step(t, 480) % 3], BB.x, BB.y);
    if (fl) { ctx.globalAlpha = 0.35; R(BB.x, BB.y, BB.w >> 1, BB.h, '#0a0c16'); ctx.globalAlpha = 1; }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
}
/** the wet road: the station, the hotel and Fed Square mirrored and rippled, then the marks, then the lights' streaks */
const STREAKS = [
  { x: 208, w: 9, col: '#ffcc88', a: 0.3, seed: 1, y0: 186 }, { x: 196, w: 4, col: '#fff0c8', a: 0.26, seed: 2, y0: 186 },
  { x: 226, w: 5, col: '#ffd090', a: 0.22, seed: 3, y0: 186 }, { x: 160, w: 6, col: '#ffc070', a: 0.18, seed: 4, y0: 184 },
  { x: 262, w: 6, col: '#ffc070', a: 0.2, seed: 5, y0: 182 }, { x: 298, w: 4, col: '#ffc070', a: 0.16, seed: 6, y0: 178 },
  { x: 410, w: 4, col: '#fff0c8', a: 0.3, seed: 7, y0: 232 }, { x: 68, w: 4, col: '#fff0c8', a: 0.3, seed: 8, y0: 246 },
  { x: 430, w: 10, col: '#ffb868', a: 0.2, seed: 9, y0: 200 }, { x: 40, w: 8, col: '#ffc888', a: 0.2, seed: 10, y0: 196 },
];
function drawRoad(t, billboard) {
  reflect(ctx, t, { top: 184, rows: 46, alpha: 0.42, squash: 1.25, x: 128, w: 214, k1: 45, k2: 110, amp: 0.5 });
  reflect(ctx, t, { top: 197, rows: 34, alpha: 0.3, squash: 1.4, x: 342, w: W - 342, k1: 40, k2: 96 });
  reflect(ctx, t, { top: 191, rows: 30, alpha: 0.3, squash: 1.3, x: 0, w: 82, k1: 48, k2: 102 });
  ctx.drawImage(MARKS_C, 0, 0);
  const sh = 1 + showerAt(t) * 0.35;
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a * sh, s.seed, s.y0, Math.min(H, s.y0 + 46));
  const s = signals(t);
  for (const S of SIGNALS) { const [x, y0] = proj(S.x, S.y, 0); streak(ctx, t, x + 1, 2, SIG_COL[s[S.veh]], 0.22, 20 + S.x, Math.round(y0) + 2, Math.round(y0) + 30); }
  if (billboard) streak(ctx, t, BB.x + 27, 6, '#e8dcc0', 0.1, 31, 198, 236);
  blit(SHELTER_C);
}
function drawStreetLife(t, crowd, wx) {
  const DL = [], s = signals(t);
  for (const T of TRAMS) {
    const f = T.cc ? ccFront(t) : tramAt(T, t);
    if (f === null) continue;
    const L = LIVERY[T.kind], mid = f - T.dir * L.len / 2, u = T.axis === 'F' ? depthOf(mid, T.lane) : depthOf(T.lane, mid);
    DL.push([u - 3, () => { T.empty = crowd === 0; drawTram(T, f, t); }]);
  }
  for (const Cr of CARS) { const c = carAt(Cr, t); if (c) DL.push([depthOf(c.x, c.y), () => drawCar(Cr, t)]); }
  for (const S of SIGNALS) DL.push([depthOf(S.x, S.y), () => drawSignal(S, s)]);
  POSTS.forEach((P, i) => DL.push([P.u, () => drawPost(P)]));
  DL.push([depthOf(BIN.x, BIN.y), () => blit(BIN_C)]);
  for (const Lc of LAMP_C) DL.push([Lc.u, () => blit(Lc)]);
  DL.push([depthOf(BUSKER.x, BUSKER.y), () => drawBusker(t, crowd)]);
  if (crowd > 0) { addCrossers(DL, t, crowd); addStayers(DL, t, crowd); addCyclist(DL, t, crowd); }
  addCart(DL, t, crowd);
  drawChipGull(DL, t);
  DL.sort((a, b) => b[0] - a[0]);
  for (const [, fn] of DL) fn();
  for (const Lc of LAMP_C) { const I = 0.92 + 0.05 * wave(t, 900, Lc.lx * 0.01); halo(HL.lamp, Lc.lx, Lc.ly + 4, I); halo(HL.lampCore, Lc.lx, Lc.ly, I); }
}

/* ================= Tap spots: boxes round what is drawn (site/js/ui/spots.js) ================= */
function boxOf(kind, pts) {
  const q = pts.map(p => proj(...p)), xs = q.map(v => v[0]), ys = q.map(v => v[1]);
  const x = Math.floor(Math.min(...xs)), y = Math.floor(Math.min(...ys));
  return { kind, x, y, w: Math.ceil(Math.max(...xs)) - x, h: Math.ceil(Math.max(...ys)) - y };
}
const SPOTS = [
  boxOf('landmark', [[ST.ax, ST.ay, 0], [ST.bx, ST.by, 0], [DOME.x, DOME.y, DOME.tip], [DOME.x - 5.2, DOME.y + 5.2, DOME.z0], [DOME.x + 5.2, DOME.y - 5.2, DOME.z0]]),   // the corner, its dome
  boxOf('bell', [[TOWER.x1, TOWER.y1 + 0.5, 0], [TOWER.x1, TOWER.y0, 0], [TOWER.x0, TOWER.y1 + 0.5, 0], [(TOWER.x0 + TOWER.x1) / 2, (TOWER.y0 + TOWER.y1) / 2, TOWER.top]]),   // the clock tower
  boxOf('window', [[YJ.x, YJ.y0 + 7.15, 3.9], [YJ.x, YJ.y0 + 9.55, 0.7]]),           // a window of the hotel's bar
];

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures); billboard: false leaves the sign out (the loop videos); weather: a
// WEATHERS pick (site/js/engine/weather.js), the city's own when left out; crowd: the share of the people to show, 0..1
// (CROWD when left out or not a number), 0 = nobody; clock: the city's time in minutes (the tower's clock)
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share, clock } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, lights = billboard ? DRZ_LIGHTS_BB : DRZ_LIGHTS;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  drawSky(t);
  ctx.drawImage(GROUND_C, 0, 0);
  drawFar(t);
  if (wx.drizzle) drawDrizzle(DRZ[0], t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawStation(t, clock);
  drawGulls(t);
  drawCorners(t, billboard, pitch);
  drawRoad(t, billboard);
  if (wx.drizzle) drawRings(t);
  ctx.drawImage(WIRES_C, 0, 0);
  if (wx.drizzle) drawDrizzle(DRZ[1], t, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawStreetLife(t, crowd, wx);
  if (wx.drizzle) drawDrizzle(DRZ[2], t, lights);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'melbourne',
  name: 'Melbourne',
  tagline: 'Drizzle under the clocks',
  timeZone: 'Australia/Melbourne',
  accent: '#00a651',                                   // the trams' green, written out: tools/seo/seo.mjs reads it from the source
  focusX: 222,
  landmark: [182, 322],                                // Flinders Street Station: the corner entrance and its dome to the clock tower
  landmarkTop: EUR.top,                                // Eureka's crown, the highest thing that must stay (the dome's lantern is lower)
  billboard: BB,
  spots: SPOTS,                                        // what answers a tap (site/js/ui/spots.js)
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { rain: 0.4, traffic: 0.35, crowd: 0.22, wind: 0.1 },
  events: [
    { t: 3, type: 'chime', x: 60, gain: 0.3 },
    { t: 25, type: 'gull', x: 300, gain: 0.32 },
    { t: 33, type: 'chime', x: 110, gain: 0.25 },
    { t: 54, type: 'chime', x: 420, gain: 0.32 },
    { t: 59, type: 'gull', x: 280, gain: 0.3 },
    { t: 91, type: 'chime', x: 440, gain: 0.3 },
    { t: CC.far, type: 'train', dur: 46, pan: [0.4, -0.9], gain: 0.18 },
    { t: CC.bell, type: 'chime', x: 300, gain: 0.45 },
    { t: CC.bell + 0.6, type: 'chime', x: 300, gain: 0.4 },
    { t: 153, type: 'chime', x: 110, gain: 0.25 },
    { t: 163, type: 'gull', x: 300, gain: 0.3 },
    { t: 173, type: 'chime', x: 420, gain: 0.3 },
    { t: 181, type: 'chime', x: 330, gain: 0.25 },
    { t: 195, type: 'gull', x: 270, gain: 0.3 },
    ...BELLS.map(t => ({ t, type: 'bell', x: 300, gain: 0.45 })),
    { t: GULLS_UP.t0 + 0.5, type: 'gull', x: 210, gain: 0.35 },
    { t: 211, type: 'chime', x: 440, gain: 0.3 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
